import TabPageHeading from '../components/Layout/TabPageHeading';
import React, { useState, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { PERMANENT_LANDING } from '../config/seoLandings';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useListCache, clearListCache } from '../hooks/useListCache';
import { CATEGORY_LABELS, countryOfBundesland, CountryCode, COUNTRY_LABELS, CORE_COUNTRIES, ALL_REGIONS as SHARED_ALL_REGIONS, regionsOfCountry, postalCodeDigits, postalCodeRegionWarning, displayBundesland } from '../config/brand';
import { findEventTypeLandingByTypes } from '../config/seoLandings';
import { getLandingContent } from '../config/landingContent';
import { CyclingEvent, CityInfo, EventCategory, EventType, DistanceRequest, EventLink, EventMapPoint, Organizer, NewOrganizerData, EVENT_TYPE_LABELS, EVENT_TYPES_BY_CATEGORY, EVENT_CATEGORY_ORDER, sortEventTypesByLabel, CATEGORY_COLORS, RegistrationMode, registrationLinkOf, withRegistrationLink , RegistrationState, radiusFilterApplies, radiusFilterPaused, NEXT_DAYS_PRESETS, nextDaysRange, matchingNextDaysPreset, dateRangeLabel, isOffSeason, filterMapPoints, weekendRange, isWeekendRange, upcomingMonths, matchingMonth, formatDayMonth, activePromotion } from '@radevents/shared';
import LocationPicker, { PickedSelection } from '../components/LocationPicker';
import communityService from '../services/communityService';
import EventZeile from '../components/EventZeile';
import EventLinksEditor, { cleanLinks } from '../components/EventLinksEditor';
import RegistrationFields from '../components/RegistrationFields';
import { umamiTrack } from '../services/analytics';
import BundeslandOptions from '../components/BundeslandOptions';
import { PrivacyNote } from '../components/PrivacyNote';
import { CalendarDays, CircleAlert, Columns2, Globe, List, Mail, Map as MapIcon, Plus, Radar, Search, Trash2, Users, X } from 'lucide-react';
import { pruefeEventVorschlag, eventVorschlagZusammenfassung, EventVorschlagFeld } from '@radevents/shared';
import SuchFeld from '../components/SuchFeld';
import ZeitraumKalender from '../components/ZeitraumKalender';
import OrtFeld from '../components/OrtFeld';
import DistanzFeld from '../components/DistanzFeld';
import EventartFeld from '../components/EventartFeld';
import LocationRadiusMap from '../components/LocationRadiusMap';
import TeilerHinweis from '../components/TeilerHinweis';
import SeasonSelect from '../components/SeasonSelect';
import AreaField from '../components/AreaField';
import EventCardHero from '../components/EventCardHero';
import EventCardBody from '../components/EventCardBody';
import EventCardStage from '../components/EventCardStage';
import { isStageCard, promoCardClass } from '../utils/eventHero';
import eventService from '../services/eventService';
import { rememberPendingFavorite } from '../services/pendingFavorite';
import organizerService from '../services/organizerService';
import api from '../services/api';
import PublicHeader from '../components/Layout/PublicHeader';
import EventVerzeichnis from '../components/Layout/EventVerzeichnis';
import PromoBannerSlot from '../components/PromoBannerSlot';
import OrganizerShortcutBanner from '../components/organizer/OrganizerShortcutBanner';
import { QuickRideHost } from '../components/winterchallenge/QuickRideDialog';
import { loadPlaces, searchPlaces, Place } from '../data/places';
import { isLocationOff, markLocationOff, markLocationOn, ortGemerkt, ortMerken, ortVergessen, ortZurPlz, plzToPrefill } from '../utils/locationFilter';
import toast from 'react-hot-toast';
import { formatDayLong } from '../utils/format';

/**
 * Werbeplatz auf dem Handy: nach dem zweiten Event zum ersten Mal, danach alle zehn. Ganz oben
 * schiebt er die Liste aus dem ersten Bildschirm, öfter macht er sie zur Anzeigenstrecke. Nach dem
 * dritten (13. bis 20.10.2026) fielen die Handy-Klicks auf Delta Bike um rund 70 %.
 */
/** Wohin „Einreichen“ bei einem Fehler springt: Feld-Id je geprüftem Feld. */
const PROPOSE_FELD_IDS: Record<EventVorschlagFeld, string> = {
  title: 'vf-title', eventDate: 'vf-date', endDate: 'vf-end', postalCode: 'vf-plz', city: 'vf-city',
  bundesland: 'vf-land', newOrganizerName: 'vf-org-name', submitterEmail: 'vf-mail',
};

const PROMO_FIRST = 2;
const PROMO_EVERY = 10;

const VALID_EVENT_TYPES = new Set<string>(Object.values(EventType));

const emptyProposeForm = {
  title: '',
  description: '',
  locationName: '',
  street: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  bundesland: '',
  eventDate: '',
  endDate: '',
  startTime: '',
  organizer: '',
  category: 'ROAD' as EventCategory,
  /** Weitere Kategorien, fuer die die Veranstaltung offen ist — ohne die Leitkategorie. */
  secondaryCategories: [] as EventCategory[],
  eventType: '' as EventType | '',
  organizerUrl: '',
  /** Anmeldestart als abgeschnittenes ISO-Datum ('2026-12', '2027'); leer = keine Angabe. */
  registrationStart: '',
  registrationMode: 'UNKNOWN' as RegistrationMode,
  registrationState: 'UNKNOWN' as RegistrationState,
  fullyBooked: false,
  cancelled: false,
  tentative: false,
  /** Permanentes Event ohne Termin — das Datum entfällt, Ort und PLZ werden optional. */
  permanent: false,
  /** Saison als Monate 1–12, beide `null` = ganzjährig. */
  seasonFrom: null as number | null,
  seasonUntil: null as number | null,
  /** Gebiet ohne festen Startort, nur bei `permanent`. */
  areaName: '',
  distances: [{ distanceKm: 0, elevationMeters: 0, routeUrl: '' }] as DistanceRequest[],
  links: [] as EventLink[],
  // Kontakt des Einreichenden (nur anonym erfragt) — das Backend meldet damit die
  // Freigabe bzw. Ablehnung zurück und ermöglicht Rückfragen.
  submitterName: '',
  submitterEmail: '',
};

// Alle Regionswerte — DE/AT/CH plus die weiteren Länder (dort ist der Landesname der Regionswert).
// Für Suchvorschläge und die Auflösung von URL-Slugs; der Footer behält seine Ländergruppen.
const ALL_REGIONS = SHARED_ALL_REGIONS;

// Anzeigename (Umlaute) zum internen ASCII-Slug: kommt aus @radevents/shared, damit die Liste
// nicht neben der gemeinsamen Quelle herläuft — sie kennt auch die weiteren Länder (Dänemark …).

const categoryLabel = (c: EventCategory) => CATEGORY_LABELS[c] || c;


// Maps URL slugs (lowercase, no umlauts) to internal Bundesland names
const BUNDESLAND_SLUG_MAP: Record<string, string> = {
  'bavaria': 'Bayern', 'bayern': 'Bayern',
  'nordrhein-westfalen': 'Nordrhein-Westfalen', 'nrw': 'Nordrhein-Westfalen',
  'niedersachsen': 'Niedersachsen',
  'bad-wuerttemberg': 'Baden-Wuerttemberg', 'bw': 'Baden-Wuerttemberg', 'baden-wuerttemberg': 'Baden-Wuerttemberg',
  'berlin': 'Berlin', 'hamburg': 'Hamburg', 'hessen': 'Hessen',
  'bremen': 'Bremen', 'saarland': 'Saarland', 'sachsen': 'Sachsen',
  'thueringen': 'Thueringen', 'sachsen-anhalt': 'Sachsen-Anhalt',
  'brandenburg': 'Brandenburg', 'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
  'rheinland-pfalz': 'Rheinland-Pfalz', 'schleswig-holstein': 'Schleswig-Holstein',
};

function slugToBundesland(slug: string): string {
  return BUNDESLAND_SLUG_MAP[slug.toLowerCase()] ||
    ALL_REGIONS.find(bl => bl.toLowerCase() === slug.toLowerCase()) || '';
}

interface LandingCache {
  key: string;
  events: CyclingEvent[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
}

interface LandingProps {
  forcedCategory?: string;
  forcedCity?: string; // pre-set city slug from route (e.g. "muenchen")
  forcedEventTypes?: string[]; // pre-set event-type filter from route (e.g. ["MARATHON", "RADMARATHON"])
  forcedCountry?: string; // pre-set country filter from route (e.g. "AT" for /radrennen-oesterreich)
  /** `/permanente-events`: nur permanente Events ohne Termin, kein Datumsfilter. */
  forcedPermanent?: boolean;
}

/**
 * „Events per Mail": Region wählen, E-Mail, Einmal-Code. Der Code legt ein unbekanntes Konto selbst
 * an und schaltet die Regions-Mails für die Region ein (`digestBundesland` am Code-Login), bei einem
 * bestehenden Konto ebenso. Bis zum 20.10.2026 stand hier ein Formular mit Benutzername und
 * Passwort, dessen Konto erst nach dem Bestätigungslink Mails bekam.
 */
function DigestRegisterModal({ bundesland, onClose }: { bundesland: string; onClose: () => void }) {
  const { requestEmailCode, loginWithEmailCode } = useAuth();
  const [step, setStep] = React.useState<'email' | 'code'>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [bl, setBl] = React.useState(bundesland || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: 8, fontSize: 14,
    border: '1px solid var(--surface-border)', background: 'var(--input-bg)',
    color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  };
  const primaryStyle: React.CSSProperties = {
    padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 700, background: '#922C57', color: '#fff',
    border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
  };
  const hintStyle: React.CSSProperties = { fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)', margin: 0 };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestEmailCode(email.trim());
      setStep('code');
    } catch (err: any) {
      setError(err?.message || 'Der Code ließ sich nicht senden.');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (value: string) => {
    if (value.length !== 6 || loading) return;
    setError('');
    setLoading(true);
    try {
      await loginWithEmailCode(email.trim(), value, { digestBundesland: bl });
      umamiTrack('newsletter-registrierung', { bundesland: bl });
      toast.success(`Ab jetzt bekommst du die Events in ${displayBundesland(bl)} per E-Mail.`);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Code ungültig oder abgelaufen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', borderRadius: 12, padding: '28px 24px', width: '100%', maxWidth: 400, position: 'relative' }}>
        <button onClick={onClose} aria-label="Schließen" style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
          <X size={18} aria-hidden />
        </button>
        <Mail size={22} color="#922C57" aria-hidden style={{ marginBottom: 8 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Events per Mail</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5 }}>
          Montags die Events der Woche{bl ? ` in ${displayBundesland(bl)}` : ''}, donnerstags die neu eingetragenen. Nur wenn es welche gibt, kostenlos und jederzeit abbestellbar.
        </p>
        {step === 'email' ? (
          <form onSubmit={sendCode} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <select required value={bl} onChange={e => setBl(e.target.value)} style={{ ...inputStyle, color: bl ? 'var(--text-primary)' : 'var(--text-muted)' }}>
              <option value="">Region wählen</option>
              <BundeslandOptions />
            </select>
            <input type="email" required autoFocus autoComplete="email" placeholder="Deine E-Mail-Adresse" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
            <p style={hintStyle}>
              Wir schicken dir einen Code, damit wir wissen, dass die Adresse dir gehört. <PrivacyNote withAccount />
            </p>
            {error && <p style={{ ...hintStyle, color: '#8C3B0B' }}>{error}</p>}
            <button type="submit" disabled={loading} style={primaryStyle}>
              {loading ? 'Sende…' : 'Code senden'}
            </button>
          </form>
        ) : (
          <form onSubmit={e => { e.preventDefault(); verify(code); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} required autoFocus
              placeholder="Code aus der E-Mail" value={code}
              onChange={e => {
                const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(next);
                if (next.length === 6) verify(next);
              }}
              style={{ ...inputStyle, letterSpacing: code ? '0.3em' : undefined, textAlign: 'center', fontSize: code ? 20 : 14 }}
            />
            <p style={hintStyle}>
              Code an <strong>{email.trim()}</strong> gesendet.{' '}
              <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#922C57', fontWeight: 700, fontSize: 12 }}>
                Ändern
              </button>
            </p>
            {error && <p style={{ ...hintStyle, color: '#8C3B0B' }}>{error}</p>}
            <button type="submit" disabled={loading} style={primaryStyle}>
              {loading ? 'Prüfe…' : 'Mails bestellen'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}


/** Mindestabstand zwischen zwei Bühnen-Cards, in Karten. */
const STAGE_MIN_GAP = 12;

/**
 * Events je Nachladen. Bis zum 30.10.2026 waren es 12, rund 1.700 px Liste: beim schnellen
 * Scrollen war man schneller am Ende als die nächste Seite da, sah kurz die Fußzeile und dann
 * schoben sich die neuen Events darunter.
 */
const SEITE = 36;

/**
 * Ausschnitt der Karte neben der Liste, solange kein Ort gesetzt ist: Deutschland mit den
 * Nachbarländern, in denen die meisten Events liegen. Mit fester Zoomstufe zeigte ein großer
 * Bildschirm halb Europa bis ins Baltikum.
 */
const KARTE_START: [[number, number], [number, number]] = [[45.9, 5.6], [54.9, 15.2]];

/**
 * Welche Card gerade unter dem Zeiger liegt, außerhalb des React-States der Seite. Gemeldet wird
 * erst, wenn der Zeiger kurz auf einer Card ruht: beim Scrollen gleiten Dutzende Cards darunter
 * durch, und jede Meldung zeichnet alle Pins der Karte neu.
 */
const HOVER_RUHE_MS = 120;
function createHoverStore() {
  let id: number | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const hoerer = new Set<() => void>();
  return {
    get: () => id,
    subscribe: (f: () => void) => { hoerer.add(f); return () => { hoerer.delete(f); }; },
    set: (neu: number | null) => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (neu !== id) { id = neu; hoerer.forEach(f => f()); } }, HOVER_RUHE_MS);
    },
  };
}
type HoverStore = ReturnType<typeof createHoverStore>;

/** Die Karte neben der Liste; liest die Card unter dem Zeiger selbst, damit nur sie neu zeichnet. */
const KarteMitHover: React.FC<Omit<React.ComponentProps<typeof LocationRadiusMap>, 'highlightId'> & { hoverStore: HoverStore }> = ({ hoverStore, ...props }) => {
  const highlightId = useSyncExternalStore(hoverStore.subscribe, hoverStore.get);
  return <LocationRadiusMap {...props} highlightId={highlightId} />;
};

const Landing: React.FC<LandingProps> = ({ forcedCategory, forcedCity, forcedEventTypes, forcedCountry, forcedPermanent = false }) => {
  usePageTitle(forcedCountry === 'AT' ? 'Radrennen & Radsport-Events in Österreich'
    : forcedCountry === 'CH' ? 'Radrennen & Radsport-Events in der Schweiz' : '');
  const navigate = useNavigate();
  const pfad = useLocation().pathname;
  const [searchParams, setSearchParams] = useSearchParams();
  const { bundesland: bundeslandSlug, city: citySlug } = useParams<{ bundesland?: string; city?: string }>();
  const bundeslandFromRoute = bundeslandSlug ? slugToBundesland(bundeslandSlug) : '';
  const citySlugFromRoute = citySlug || forcedCity || '';
  const { isAuthenticated, isLoading: authLoading, user, isAdminOrModerator } = useAuth();
  const { cache: initialCache, setCache: setLandingCache, fromCache: suppressAnim } = useListCache<LandingCache>('landing', user?.id ?? 'anonymous');
  const [events, setEvents] = useState<CyclingEvent[]>(initialCache?.events ?? []);
  const [eventsOwner, setEventsOwner] = useState(user?.id);
  const [loading, setLoading] = useState(!initialCache);
  const { saveScrollState, loadScrollState } = useScrollRestoration(!loading);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialCache?.currentPage ?? 0);
  const [totalPages, setTotalPages] = useState(initialCache?.totalPages ?? 0);
  const [totalElements, setTotalElements] = useState(initialCache?.totalElements ?? 0);
  const [selectedBundesland, setSelectedBundesland] = useState<string>(bundeslandFromRoute || searchParams.get('bl') || '');
  // Länderfilter (DE/AT). Vom /radrennen-oesterreich-Route via forcedCountry vorbelegt, sonst URL.
  // Faellt auf das Land des vorbelegten Bundeslands zurueck: SEO-Einstiege wie /rtf-events/Niedersachsen
  // liefern nur das Bundesland, ohne Land bliebe der Laender-Chip unmarkiert und das Bundesland-Dropdown
  // (haengt an selectedCountry) unsichtbar — der aktive Filter waere also nicht bedienbar.
  const [selectedCountry, setSelectedCountry] = useState<'' | CountryCode>(
    (forcedCountry as CountryCode) || (searchParams.get('country') as CountryCode)
    || countryOfBundesland(bundeslandFromRoute || searchParams.get('bl')) || ''
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(forcedCategory || searchParams.get('cat') || '');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(() => {
    const forced = (forcedEventTypes || []).filter(t => VALID_EVENT_TYPES.has(t));
    if (forced.length) return forced;
    return (searchParams.get('type') || '').split(',').map(s => s.trim()).filter(t => VALID_EVENT_TYPES.has(t));
  });
  // Abhängige Eventart-Auswahl (Popover/Panel offen + Suchbegriff)
  const [eaOpen, setEaOpen] = useState(false);
  const [eaQuery, setEaQuery] = useState('');
  const [availableBundeslaender, setAvailableBundeslaender] = useState<string[]>([]);
  const [cities, setCities] = useState<CityInfo[]>([]);
  // Resolve city slug from route to actual city name (e.g. "muenchen" → "München")
  const [selectedCity, setSelectedCity] = useState<string>(searchParams.get('city') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  // Swipe-to-close des mobilen Filter-Sheets: Drag am Grab-Handle zieht das Sheet mit,
  // ab genug Zugweg wird geschlossen, sonst federt es zurück (Transition aus dem CSS).
  const filterSheetRef = useRef<HTMLElement>(null);
  const sheetDragStartY = useRef<number | null>(null);
  const sheetDragDelta = useRef(0);
  const onSheetHandleTouchStart = (e: React.TouchEvent) => {
    sheetDragStartY.current = e.touches[0].clientY;
    sheetDragDelta.current = 0;
    if (filterSheetRef.current) filterSheetRef.current.style.transition = 'none';
  };
  const onSheetHandleTouchMove = (e: React.TouchEvent) => {
    if (sheetDragStartY.current == null || !filterSheetRef.current) return;
    const delta = Math.max(0, e.touches[0].clientY - sheetDragStartY.current);
    sheetDragDelta.current = delta;
    filterSheetRef.current.style.transform = `translateY(${delta}px)`;
  };
  const onSheetHandleTouchEnd = () => {
    const sheet = filterSheetRef.current;
    if (!sheet || sheetDragStartY.current == null) return;
    // Inline-Styles entfernen → CSS übernimmt wieder (animiert zurück bzw. nach unten raus).
    sheet.style.transition = '';
    sheet.style.transform = '';
    if (sheetDragDelta.current > 80) setMobileFilterOpen(false);
    sheetDragStartY.current = null;
    sheetDragDelta.current = 0;
  };
  const [minKmInput, setMinKmInput] = useState(searchParams.get('minKm') || '');
  const [maxKmInput, setMaxKmInput] = useState(searchParams.get('maxKm') || '');
  const [minKm, setMinKm] = useState(searchParams.get('minKm') || '');
  const [maxKm, setMaxKm] = useState(searchParams.get('maxKm') || '');
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || '');
  const [dateInput, setDateInput] = useState(searchParams.get('date') || '');
  // Ende des Zeitraums („bis"). Zusammen mit dem „ab" ergibt es einen Zeitraum; die Schnellwahl
  // „nächste X Tage" setzt einfach beide Werte (siehe shared/dateRange).
  const [selectedDateTo, setSelectedDateTo] = useState(searchParams.get('dateTo') || '');
  // Die Liste der permanenten Events. Ein Zeitraum ergibt dort keinen Sinn,
  // deshalb faellt ein per URL mitgebrachtes Datum weg, statt still ignoriert zu werden.
  const permanentView = forcedPermanent;
  useEffect(() => {
    if (permanentView) { setSelectedDate(''); setDateInput(''); setSelectedDateTo(''); }
  }, [permanentView]);
  // Events in Saisonpause sind in der Liste „Permanent" das, was vergangene Termine in der
  // Terminliste sind: ausgeblendet, bis jemand sie ausdrücklich sehen will.
  const [showOffSeason, setShowOffSeason] = useState(false);
  // Eine im Ortsfeld getippte PLZ: angemeldet nur für die laufende Sitzung (sessionStorage), das
  // Profil bleibt unberührt (30.10.2026, vorher überschrieb sie still die PLZ im Konto). Ohne
  // Konto gibt es kein Profil, dort merkt sich der Browser sie dauerhaft.
  const [userPlz, setUserPlz] = useState<string>(() => ortGemerkt('radevents.userPlz') ?? '');
  const [userLat, setUserLat] = useState<number | null>(() => {
    const v = ortGemerkt('radevents.userLat');
    return v ? parseFloat(v) : null;
  });
  const [userLng, setUserLng] = useState<number | null>(() => {
    const v = ortGemerkt('radevents.userLng');
    return v ? parseFloat(v) : null;
  });
  const [selectedRadius, setSelectedRadius] = useState<number | null>(() => {
    const r = searchParams.get('radius');
    return r ? Number(r) : null;
  });
  const [plzGeocoding, setPlzGeocoding] = useState(false);
  const [plzError, setPlzError] = useState(false);

  // ── Ort & Umkreis auf der Karte wählen ─────────────────────────────────────
  const [mapOpen, setMapOpen] = useState(false);
  const [mapPoints, setMapPoints] = useState<EventMapPoint[]>([]);
  const [mapPointsLoading, setMapPointsLoading] = useState(false);
  // Die permanenten Events als zuschaltbare zweite Ebene der Terminkarte — eigener Satz, weil die
  // Terminliste ihre Trefferzahl nur aus den Terminen rechnet.
  const [permanentMapPoints, setPermanentMapPoints] = useState<EventMapPoint[]>([]);
  const permanentLayerRequested = useRef(false);
  // Wechselt bei jeder übernommenen Ortswahl — auch wenn Name und Radius gleich bleiben. Ohne den
  // Schlüssel bliebe ein per Karte nur verschobenes Zentrum ohne Wirkung: kein Dep der Ladeschleife
  // hätte sich geändert, die Liste zeigte weiter die Events des alten Punkts.
  const [placeKey, setPlaceKey] = useState('');
  // Phone-Layout: entscheidet zwischen Vollbild-Ort-Screen und Desktop-Modal und blendet die
  // Standort-Felder aus dem Filter-Sheet aus (dort läuft der Ort ausschließlich über die Karte).
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width:768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width:768px)');
    const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);


  /**
   * Die eigenen Favoriten als Ids — die Karte hebt sie hervor.
   *
   * Getrennt von `events` geladen, weil die Karte *alle* kommenden Events zeigt: die geladene
   * Listenseite weiß nur von ihren eigenen 20 Einträgen, welche davon markiert sind.
   */
  /**
   * „Mit Freunden“: nur Events, bei denen bestätigte Freunde dabei sind (`friendsOnly`, PR #197).
   * Angeboten nur angemeldet und mit mindestens einem Freund (`hasFriends`), sonst wäre die Liste
   * immer leer. Steht in der URL (`friends=1`), damit ein Link die Auswahl behält.
   */
  const [friendsOnlyWanted, setFriendsOnly] = useState(searchParams.get('friends') === '1');
  const [hasFriends, setHasFriends] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setHasFriends(false); return; }
    communityService.overview()
      .then(o => setHasFriends(o.people.some(p => p.state === 'FRIEND')))
      .catch(() => setHasFriends(false));
  }, [isAuthenticated, user?.id]);
  const friendsOnly = friendsOnlyWanted && isAuthenticated && !permanentView;
  // Die Karte kennt keine Freunde (`map-points` ist für alle gleich). Mit „Mit Freunden“ zeigt sie
  // deshalb nur die Events aus der Freundesliste der Planung, dieselbe Menge wie die Liste.
  const [friendEventIds, setFriendEventIds] = useState<Set<number> | null>(null);
  useEffect(() => {
    if (!friendsOnly) { setFriendEventIds(null); return; }
    communityService.planning('friends')
      .then(list => setFriendEventIds(new Set(list.map(e => e.id))))
      .catch(() => setFriendEventIds(new Set()));
  }, [friendsOnly, user?.id]);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const loadFavoriteIds = () => {
    if (!isAuthenticated) { setFavoriteIds(new Set()); return; }
    eventService.getFavoriteIds()
      .then(ids => setFavoriteIds(new Set(ids)))
      .catch(() => { /* Ohne Favoriten sieht die Karte aus wie für Gäste — kein Grund für eine Meldung. */ });
  };

  // Kartenpunkte erst laden, wenn die Karte zum ersten Mal geöffnet wird — sie sind für die
  // Ergebnisliste irrelevant und würden den Erstaufruf der Startseite unnötig belasten.
  // Die Liste „Permanent" zeigt andere Punkte als die Terminliste (permanente Events, Gebiete als
  // Fläche) — gemerkt wird deshalb, für welche Liste die geladenen Punkte gelten.
  const mapPointsRequested = useRef<'termine' | 'permanent' | null>(null);
  const openMapPicker = () => {
    setMapOpen(true);
    // Die Favoriten dagegen jedes Mal: sie ändern sich zwischen zwei Kartenöffnungen (Herz in der
    // Liste, zweiter Tab), und es ist eine Handvoll Zahlen.
    loadFavoriteIds();
    if (!permanentView && !permanentLayerRequested.current) {
      permanentLayerRequested.current = true;
      eventService.getMapPoints(true)
        .then(setPermanentMapPoints)
        // Ohne die Ebene bleibt die Terminkarte vollständig; beim nächsten Öffnen erneut versuchen.
        .catch(() => { permanentLayerRequested.current = false; });
    }
    ensureMapPoints();
  };
  /** Kartenpunkte der aktuellen Liste, einmal je Liste. Braucht der Ort-Dialog und die Startkarte. */
  const ensureMapPoints = () => {
    const mode = permanentView ? 'permanent' : 'termine';
    if (mapPointsRequested.current === mode) return;
    mapPointsRequested.current = mode;
    setMapPoints([]);
    setMapPointsLoading(true);
    eventService.getMapPoints(mode === 'permanent')
      .then(points => { if (mapPointsRequested.current === mode) setMapPoints(points); })
      // Bei einem Fehler darf es beim nächsten Öffnen erneut versucht werden.
      .catch(() => { if (mapPointsRequested.current === mode) mapPointsRequested.current = null; })
      .finally(() => setMapPointsLoading(false));
  };

  const applyPickedLocation = (picked: PickedSelection) => {
    // Der Ort liefert jetzt die Koordinaten; eine noch gesetzte PLZ wäre widersprüchlich.
    plzClearedByPlaceRef.current = true;
    setUserPlz('');
    if (picked.radiusKm == null) {
      // Umkreis in der Karte ausgeschaltet: der Standort verschwindet mit ihm. Ein Ort ohne Weite
      // filtert nichts — er stünde nur als Filterchip da und verspräche eine Einschränkung, die
      // es nicht gibt. Die Filter aus der Karte gelten trotzdem (siehe unten).
      //
      // Die Stadt geht mit: die Karte kann sie nicht mitzählen (sie steht nicht im Kartenpunkt),
      // ihre Zahl wäre sonst eine andere als die der Liste — und genau das soll der CTA nicht.
      markLocationOff();
      setPlaceName('');
      setUserLat(null);
      setUserLng(null);
      setSelectedRadius(null);
      setSelectedCity('');
      setPlaceKey('');
    } else {
      markLocationOn();
      // Ein Umkreis ersetzt das Bundesland (und umgekehrt, s. `selectBundesland`): beides
      // zugleich ergäbe die Schnittmenge, meist eine fast leere Liste.
      setSelectedBundesland('');
      setPlaceName(picked.name);
      setUserLat(picked.lat);
      setUserLng(picked.lng);
      setSelectedRadius(picked.radiusKm);
      setPlaceKey(`${picked.lat.toFixed(4)},${picked.lng.toFixed(4)},${picked.radiusKm}`);
    }
    // Kategorie, Datum und Distanz kommen aus der Karte mit — nur so löst der CTA („N Events
    // anzeigen") seine Zahl auch ein. Eine gewechselte Kategorie räumt die Eventarten weg, die
    // ja an der alten hingen (gleiche Regel wie in der Filter-Sidebar).
    if (picked.filters.category !== selectedCategory) setSelectedEventTypes([]);
    setSelectedCategory(picked.filters.category);
    setSelectedDate(picked.filters.date);
    setDateInput(picked.filters.date);
    setMinKmInput(picked.filters.minKm);
    setMaxKmInput(picked.filters.maxKm);
    // Die Km-Felder laufen sonst erst über ihren Tippschutz (400 ms) in den Filter — hier ist die
    // Eingabe längst fertig, und die Liste soll mit derselben Zahl laden, die der Knopf nannte.
    setMinKm(picked.filters.minKm);
    setMaxKm(picked.filters.maxKm);
    setMapOpen(false);
  };

  /**
   * „Anzeigen" an der Zeile der permanenten Events: dieselbe Übernahme wie „Fertig", danach in die
   * Liste „Permanent". Ort und Umkreis gehen mit, weil beide Adressen dieselbe `Landing`-Instanz
   * rendern (wie beim Umschalter über der Liste) und der Umkreis ohnehin in der URL steht.
   */
  const showPermanentNearby = (picked: PickedSelection) => {
    applyPickedLocation(picked);
    navigate({ pathname: `/${PERMANENT_LANDING.slug}`, search: window.location.search });
  };

  /**
   * Filter, die der Ort-Dialog nicht selbst anbietet, aber trotzdem gelten — sonst verspräche der
   * CTA („N Events anzeigen") eine Zahl, die die Liste danach nicht einlöst. Kategorie, Datum und
   * Distanz stehen im Dialog und werden dort gerechnet; Freitextsuche und Stadt lassen sich auf
   * einem Kartenpunkt nicht prüfen — sind sie gesetzt, bleibt die Zahl eine Obergrenze.
   */
  const mapLockedFilters = useMemo(
    () => ({ bundesland: selectedBundesland || null, country: selectedCountry || null, dateTo: selectedDateTo || null, includeOffSeason: permanentView && showOffSeason }),
    [selectedBundesland, selectedCountry, selectedDateTo, permanentView, showOffSeason]
  );

  // Nur in der Liste „Permanent“; Wortlaut wie „Vergangene zeigen“ in Merkliste und Veranstalter-Bereich.
  const offSeasonToggle = permanentView ? (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#6A6A6A', cursor: 'pointer' }}>
      <input type="checkbox" checked={showOffSeason} onChange={e => setShowOffSeason(e.target.checked)} />
      Events außerhalb der Saison anzeigen
    </label>
  ) : null;

  const clearLocation = () => {
    markLocationOff();
    setPlaceName('');
    setUserPlz('');
    setSelectedCity('');
    setUserLat(null);
    setUserLng(null);
    setSelectedRadius(null);
    setPlaceKey('');
  };

  // Mobile combined-search autocomplete
  const [searchFocus, setSearchFocus] = useState(false);
  // Per Tastatur hervorgehobener Vorschlag (-1 = keiner); Index ueber die flache suggestOptions-Liste.
  const [activeSuggest, setActiveSuggest] = useState(-1);
  const [eventSuggest, setEventSuggest] = useState<CyclingEvent[]>([]);
  const [placeName, setPlaceName] = useState('');  // place chosen from autocomplete (radius origin)
  const [places, setPlaces] = useState<Place[] | null>(null);  // lazy-loaded DE places dataset
  const mobileSearchRef = useRef<HTMLDivElement | null>(null);
  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const focusSearch = () => { setSearchFocus(true); if (!places) loadPlaces().then(setPlaces).catch(() => {}); };

  const [showProposeModal, setShowProposeModal] = useState(false);
  const [showDigestModal, setShowDigestModal] = useState(false);
  const [proposeForm, setProposeForm] = useState(emptyProposeForm);
  const [proposeEditEventId, setProposeEditEventId] = useState<number | null>(null);
  const [proposeSaving, setProposeSaving] = useState(false);
  /** Einmal auf „Einreichen“ gedrückt: ab dann stehen die Fehler an den Feldern und gehen beim Tippen weg. */
  const [proposeVersucht, setProposeVersucht] = useState(false);
  /** Fehler vom Server (z. B. Dublette), steht über dem Knopf statt nur als Toast. */
  const [proposeServerFehler, setProposeServerFehler] = useState<string | null>(null);

  // Hintergrund-Scroll sperren, solange ein Vollbild-Modal offen ist — sonst scrollt
  // auf dem Handy der Hintergrund statt des Modal-Inhalts und der Inhalt wird unerreichbar.
  useEffect(() => {
    if (!showProposeModal && !showDigestModal && !mobileFilterOpen && !mapOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // .modal-open blendet die Tab-Bar aus (verdeckt sonst die Buttons) und sperrt via CSS
    // overscroll-behavior das native Pull-to-Refresh, das sonst das Modal-Scrollen zerschießt.
    document.body.classList.add('modal-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('modal-open');
    };
  }, [showProposeModal, showDigestModal, mobileFilterOpen, mapOpen]);

  // Organizer state for propose modal
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [organizerMode, setOrganizerMode] = useState<'existing' | 'new' | 'freetext'>('freetext');
  const [selectedOrganizerId, setSelectedOrganizerId] = useState<number | null>(null);
  const [organizerSearch, setOrganizerSearch] = useState('');
  const [newOrganizerName, setNewOrganizerName] = useState('');
  const [newOrganizerContactPerson, setNewOrganizerContactPerson] = useState('');
  const [newOrganizerEmail, setNewOrganizerEmail] = useState('');
  const [newOrganizerPhone, setNewOrganizerPhone] = useState('');
  const [newOrganizerWebsite, setNewOrganizerWebsite] = useState('');

  const sentinelRef = useRef<HTMLDivElement>(null);
  // Höhe einer Listenzeile samt Abstand, gemessen an der geladenen Liste: danach richtet sich der
  // Platz, den die Liste für die nächste Seite freihält.
  const zeilenRef = useRef<HTMLDivElement>(null);
  const [zeilenHoehe, setZeilenHoehe] = useState(130);

  // Effective location: PLZ-based coords take priority; fallback to logged-in user's profile coords
  const effectiveLat = userLat ?? user?.latitude ?? null;
  const effectiveLng = userLng ?? user?.longitude ?? null;
  // Ohne Koordinaten schraenkt eine Weite nichts ein — dieselbe Bedingung, die die Ladeschleife
  // frueher inline gerechnet hat.
  const radiusActive = effectiveLat != null && effectiveLng != null && selectedRadius != null;
  // Eine Textsuche setzt den Umkreis aus (Regel in shared, damit die App dieselbe Menge zeigt).
  // Der Standort bleibt dabei gesetzt und greift wieder, sobald das Suchfeld leer ist — deshalb
  // „pausiert" und nicht „geloescht". Filter-Chips, Seitenleiste und Liste muessen dasselbe sagen,
  // sonst steht neben einem Treffer aus Tirol ein Chip „100 km Umkreis", der ihn ausschliesst.
  const radiusPaused = radiusFilterPaused(effectiveLat, effectiveLng, selectedRadius, searchQuery);
  const radiusApplies = radiusFilterApplies(effectiveLat, effectiveLng, selectedRadius, searchQuery);

  const eventRequestSequence = useRef(0);
  const loadEvents = async (page: number, append: boolean = false, search: string = '', bundesland?: string, date?: string, distMinKm?: string, distMaxKm?: string, city?: string, lat?: number, lng?: number, radius?: number, category?: string, eventType?: string, size?: number) => {
    const requestSequence = ++eventRequestSequence.current;
    if (page === 0 && !append) {
      // Don't clear events immediately — keep old list visible while loading to prevent
      // page height collapse (which would reset scroll position to 0)
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const minKmNum = distMinKm ? Number(distMinKm) : undefined;
      const maxKmNum = distMaxKm ? Number(distMaxKm) : undefined;
      const result = await eventService.getAll(page, size ?? SEITE, undefined, bundesland || undefined, lat, lng, radius, search || undefined, permanentView ? undefined : (date || undefined), minKmNum, maxKmNum, undefined, category || undefined, city || undefined, eventType || undefined, undefined, undefined, undefined, undefined, undefined, selectedCountry || undefined, undefined, permanentView ? undefined : (selectedDateTo || undefined), permanentView, permanentView && showOffSeason, friendsOnly);
      if (requestSequence !== eventRequestSequence.current) return;
      const content = result?.content ?? [];
      setEventsOwner(user?.id);
      setEvents(prev => append ? [...prev, ...content] : content);
      setCurrentPage(size && size > SEITE ? Math.ceil(size / SEITE) - 1 : result.page);
      setTotalPages(result.totalPages);
      setTotalElements(result.totalElements);
    } catch {
      if (requestSequence === eventRequestSequence.current) toast.error('Events konnten nicht geladen werden');
    } finally {
      if (requestSequence === eventRequestSequence.current) { setLoading(false); setLoadingMore(false); }
    }
  };

  useEffect(() => {
    // A 5-digit PLZ is treated purely as a location (radius origin), not as a free-text query.
    const timer = setTimeout(() => setSearchQuery(/^\d{5}$/.test(searchInput.trim()) ? '' : searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => { setMinKm(minKmInput); setMaxKm(maxKmInput); }, 400);
    return () => clearTimeout(timer);
  }, [minKmInput, maxKmInput]);

  // Pre-fill PLZ input from user profile (extract first 5-digit sequence)
  useEffect(() => {
    // isLocationOff(): wer den Standort in dieser Sitzung weggeklickt hat, bekommt ihn hier nicht
    // wieder untergeschoben — sonst haelt die Abwahl nur bis zum naechsten Seitenwechsel.
    // Steht schon ein Bundesland oder ein anderes Land fest (SEO-Einstieg wie /rtf-events/Bayern,
    // `?bl=`, /radrennen-oesterreich), bleibt der Umkreis um zuhause weg: beides zugleich ergäbe
    // die Schnittmenge, für Bayern mit 100 km um Hannover eine fast leere Liste.
    if (selectedBundesland || (selectedCountry && selectedCountry !== 'DE')) return;
    const plz = plzToPrefill(user?.location, userPlz, isLocationOff());
    if (plz) setUserPlz(plz);
  // userPlz absichtlich ausgelassen: die Vorbefuellung soll nur greifen, solange noch keine PLZ
  // gesetzt ist. Als Dep wuerde der Effekt bei jeder Eingabe erneut laufen und die Profil-PLZ
  // ueber das gerade Getippte schreiben.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.location]);

  // PLZ geocoding: debounce 500ms, only on exactly 5 digits
  const prevUserPlzRef = useRef(userPlz);
  // Gesetzt, wenn selectPlace die PLZ absichtlich leert: der Ort liefert die Koordinaten, die
  // PLZ ist damit hinfaellig. Ohne dieses Flag liest der Effekt unten "PLZ von '12345' auf ''"
  // als "User hat seine PLZ geloescht" und setzt lat/lng/radius wieder auf null — der gerade
  // gewaehlte Ort waere sofort wieder entwertet (Pille sichtbar, aber Suche deutschlandweit).
  const plzClearedByPlaceRef = useRef(false);
  useEffect(() => {
    // Austria and Switzerland use 4-digit postal codes, Germany 5.
    const plzLen = selectedCountry === 'AT' || selectedCountry === 'CH' ? 4 : 5;
    const prevPlz = prevUserPlzRef.current;
    prevUserPlzRef.current = userPlz;
    if (userPlz === '') {
      const clearedByPlace = plzClearedByPlaceRef.current;
      plzClearedByPlaceRef.current = false;
      // Only clear coords/radius if the user actively emptied a previously set PLZ.
      // On initial mount (logged-in users have PLZ in profile, not localStorage), userPlz
      // starts empty — we must not wipe selectedRadius before pre-fill from profile runs.
      if (prevPlz !== '' && !clearedByPlace) {
        markLocationOff();
        setUserLat(null);
        setUserLng(null);
        setSelectedRadius(null);
        setPlaceKey('');
        setPlzError(false);
        ortVergessen();
      }
      return;
    }
    // Incomplete input: clear coords so stale location doesn't stay active
    if (!new RegExp(`^\\d{${plzLen}}$`).test(userPlz)) {
      setUserLat(null);
      setUserLng(null);
      setPlzError(false);
      return;
    }
    // Vollstaendige PLZ = der Nutzer will wieder einen Umkreis. Auch fuer den Fall darunter
    // wichtig, in dem die Profil-Koordinaten schon passen und der Effekt vorzeitig aussteigt.
    markLocationOn();
    // Logged-in user: if PLZ matches what's already geocoded in profile, skip API call
    if (isAuthenticated && user?.latitude != null) {
      const profilePlz = user.location?.match(/\d{5}/)?.[0];
      if (profilePlz === userPlz) {
        setPlzError(false);
        // Die Koordinaten stehen schon im Profil, also keine Abfrage. Der Umkreis muss trotzdem
        // gesetzt werden: ohne ihn filterte die PLZ aus dem Konto nichts, und die Liste begann
        // entgegen `locationFilter.ts` nicht „mit Umkreis um zuhause“ (Fund vom 26.10.2026).
        setSelectedRadius(r => (r == null ? 100 : r));
        return;
      }
    }
    const timer = setTimeout(async () => {
      setPlzGeocoding(true);
      setPlzError(false);
      try {
        const res = await api.get(`/geocode/plz/${userPlz}`, { params: { country: selectedCountry || 'DE' } });
        const { lat, lng } = res.data;
        setUserLat(lat);
        setUserLng(lng);
        // Die Ladeschleife hört bewusst nicht auf effectiveLat/Lng (jede Ziffer würde sonst neu
        // laden) — ohne diesen Schlüssel bliebe die Liste beim alten Standort stehen, sobald der
        // Umkreis schon stand: dann änderte sich beim Wechsel der PLZ keine einzige Dependency.
        setPlaceKey(`${lat},${lng}`);
        // Eine PLZ ohne Umkreis filtert nichts — deshalb einen sinnvollen Default setzen, sobald
        // ein Standort vorliegt (identisch zur Orts-Autocomplete). Eine bereits gewählte Weite bleibt.
        setSelectedRadius(r => (r == null ? 100 : r));
        ortMerken(isAuthenticated, userPlz, lat, lng);
      } catch {
        setPlzError(true);
        setUserLat(null);
        setUserLng(null);
      } finally {
        setPlzGeocoding(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  // user absichtlich ausgelassen: er wird nur lesend benutzt. Der Effekt soll nur auf PLZ,
  // Login-Status und Land reagieren, nicht auf jede Aenderung am Konto.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlz, isAuthenticated, selectedCountry]);

  // Load cities once for slug resolution + place autocomplete
  useEffect(() => {
    eventService.getCities().then(setCities).catch(() => {});
  }, []);

  // Event suggestions for the combined mobile search (debounced), skipped for PLZ input
  useEffect(() => {
    const q = searchInput.trim();
    if (!searchFocus || q.length < 2 || /^\d{5}$/.test(q)) { setEventSuggest([]); return; }
    const timer = setTimeout(() => {
      eventService.getAll(0, 6, undefined, undefined, undefined, undefined, undefined, q)
        .then(res => setEventSuggest(res.content))
        .catch(() => setEventSuggest([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput, searchFocus]);

  // Close the autocomplete when tapping outside the search field (mobile or desktop)
  useEffect(() => {
    if (!searchFocus) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      const inMobile = mobileSearchRef.current?.contains(t);
      const inDesktop = desktopSearchRef.current?.contains(t);
      if (!inMobile && !inDesktop) setSearchFocus(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [searchFocus]);

  // Resolve city slug → city name once cities are loaded
  useEffect(() => {
    if (!citySlugFromRoute || cities.length === 0) return;
    const match = cities.find(c => c.slug === citySlugFromRoute);
    if (match) setSelectedCity(match.name);
  }, [citySlugFromRoute, cities]);

  useEffect(() => {
    if (authLoading) return;
    // Don't load events yet if URL has a city slug but cities haven't loaded —
    // selectedCity will be empty briefly and would cause a refetch+flicker
    // when it later resolves to the city name.
    if (citySlugFromRoute && cities.length === 0) return;
    const hasRadius = radiusApplies;
    // Die Seitengröße gehört zum Schlüssel: eine Liste aus 12er-Seiten zählt ihre Seiten anders.
    const cacheKey = JSON.stringify({ seite: SEITE, friendsOnly, userId: user?.id,
      searchQuery, selectedBundesland, selectedDate, dateTo: selectedDateTo, minKm, maxKm, selectedCity, permanentView, showOffSeason,
      lat: hasRadius ? effectiveLat : null, lng: hasRadius ? effectiveLng : null,
      radius: hasRadius ? selectedRadius : null,
      category: selectedCategory, eventType: selectedEventTypes.join(','),
    });
    // If cache matches current filters, hydrate from cache (skip refetch on back-nav)
    if (initialCache && initialCache.key === cacheKey) {
      setEventsOwner(user?.id);
      setEvents(initialCache.events);
      setCurrentPage(initialCache.currentPage);
      setTotalPages(initialCache.totalPages);
      setTotalElements(initialCache.totalElements);
      setLoading(false);
      return;
    }
    const savedState = loadScrollState();
    // Auf volle Seiten aufrunden, sonst fehlen nach dem Zurückkehren die Events bis zum Seitenende.
    const restoredSize = savedState?.eventCount && savedState.eventCount > SEITE ? Math.ceil(savedState.eventCount / SEITE) * SEITE : undefined;
    loadEvents(0, false, searchQuery, selectedBundesland, selectedDate, minKm, maxKm, selectedCity,
      hasRadius ? effectiveLat! : undefined,
      hasRadius ? effectiveLng! : undefined,
      hasRadius ? selectedRadius! : undefined,
      selectedCategory || undefined,
      selectedEventTypes.join(',') || undefined,
      restoredSize,
    );
  // effectiveLat/effectiveLng intentionally omitted: entering a PLZ should not trigger a reload.
  // When selectedRadius changes, effectiveLat/Lng are read from closure at that moment.
  // placeName IS a dep: ohne sie wechselt die Auswahl von Ort A auf Ort B zwar die Koordinaten,
  // aber kein Dep aendert sich (der Radius steht ja schon) — die Liste zeigte weiter A's Events,
  // waehrend die Ueberschrift B verspricht.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, friendsOnly, authLoading, searchQuery, selectedBundesland, selectedCountry, selectedCategory, selectedEventTypes, selectedDate, selectedDateTo, minKm, maxKm, selectedCity, selectedRadius, placeName, placeKey, permanentView, showOffSeason]);

  // Umami: Filter-/Suchnutzung als ein Event mit Payload (nur aktive Filter).
  // Erster Lauf wird übersprungen — der bildet nur den per URL wiederhergestellten Zustand ab.
  const filterTracked = useRef(false);
  useEffect(() => {
    if (!filterTracked.current) { filterTracked.current = true; return; }
    const data: Record<string, string | number> = {};
    if (searchQuery) data.suche = searchQuery;
    if (selectedBundesland) data.bundesland = selectedBundesland;
    if (selectedCountry) data.land = selectedCountry;
    if (selectedCategory) data.kategorie = selectedCategory;
    if (selectedEventTypes.length) data.eventart = selectedEventTypes.join(',');
    if (selectedDate) data.datum = selectedDate;
    if (selectedDateTo) data.bis = selectedDateTo;
    if (minKm) data.minKm = minKm;
    if (maxKm) data.maxKm = maxKm;
    if (selectedCity) data.ort = selectedCity;
    if (selectedRadius != null) data.radius = selectedRadius;
    if (Object.keys(data).length) umamiTrack('filter', data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendsOnly, selectedBundesland, selectedCountry, selectedCategory, selectedEventTypes, searchQuery, selectedDate, selectedDateTo, minKm, maxKm, selectedCity, selectedRadius]);

  // Sync filters to URL params so they survive navigation
  useEffect(() => {
    const params = new URLSearchParams();
    if (friendsOnly) params.set('friends', '1');
    if (selectedBundesland) params.set('bl', selectedBundesland);
    if (selectedCountry) params.set('country', selectedCountry);
    if (selectedCategory) params.set('cat', selectedCategory);
    if (selectedEventTypes.length) params.set('type', selectedEventTypes.join(','));
    if (searchQuery) params.set('q', searchQuery);
    if (selectedDate) params.set('date', selectedDate);
    if (selectedDateTo) params.set('dateTo', selectedDateTo);
    if (minKm) params.set('minKm', minKm);
    if (maxKm) params.set('maxKm', maxKm);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedRadius != null) params.set('radius', String(selectedRadius));
    setSearchParams(params, { replace: true });
  // setSearchParams absichtlich ausgelassen: die Funktion ist pro Render neu und wuerde den Effekt
  // nach jedem eigenen setSearchParams erneut ausloesen (Render-Schleife). Er soll nur auf Filter reagieren.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendsOnly, selectedBundesland, selectedCountry, selectedCategory, selectedEventTypes, searchQuery, selectedDate, selectedDateTo, minKm, maxKm, selectedCity, selectedRadius]);

  useEffect(() => {
    eventService.getBundeslaender().then(setAvailableBundeslaender).catch(() => {});
    organizerService.listPublic().then(setOrganizers).catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading || !selectedBundesland) return;
    const key = `digest-toast-${selectedBundesland}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    const bl = selectedBundesland;
    const handleClick = () => {
      if (isAuthenticated) {
        navigate('/profile/notifications');
      } else {
        setShowDigestModal(true);
      }
    };
    toast.custom((t) => (
      <div
        onClick={() => { toast.remove(t.id); handleClick(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px 14px 18px',
          background: '#922C57', borderRadius: 14, position: 'relative',
          boxShadow: '0 8px 24px -4px rgba(146,44,87,0.45)',
          cursor: 'pointer', width: 'min(92vw, 420px)', fontSize: 14,
          animation: 'fadeInDown 0.25s ease',
          touchAction: 'pan-y',
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); toast.remove(t.id); }}
          style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#D08BA3', fontSize: 18, padding: 4, lineHeight: 1 }}
        >✕</button>
        <span style={{ fontSize: 24, flexShrink: 0 }}>📬</span>
        <div style={{ flex: 1, paddingRight: 16 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}>Events in {bl} per Mail erhalten?</div>
          <div style={{ fontSize: 12, color: '#E3C0CE' }}>Kostenlos anmelden – jederzeit abbestellbar</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
          Anmelden →
        </span>
      </div>
    ), { duration: 12000, position: 'top-center' });
  // navigate absichtlich ausgelassen: wird nur im Klick-Handler des Toasts benutzt. Der Toast soll
  // pro Bundesland genau einmal erscheinen (sessionStorage-Guard) und nicht durch Router-Wechsel neu laufen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBundesland, isAuthenticated, authLoading]);

  useEffect(() => {
    const liste = zeilenRef.current;
    if (!liste || events.length < 6) return;
    const h = (liste.offsetHeight + 10) / liste.children.length;
    if (h > 40 && Math.abs(h - zeilenHoehe) > 4) setZeilenHoehe(Math.round(h));
  // zeilenHoehe absichtlich ausgelassen: gemessen wird, wenn neue Events dazukommen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    // Nachladen, sobald das Ende der geladenen Events weniger als 2.500 px unter dem Fensterrand
    // liegt, oder schon darüber: wer schneller scrollt, als geladen wird, steht unter dem Anker im
    // freigehaltenen Platz, und auch dann muss die nächste Seite kommen. Gefragt wird nach der Lage
    // statt per IntersectionObserver, weil die Seite in `body` scrollt und der Beobachter einen
    // weggescrollten Anker dort nicht mehr meldet; geprüft wird beim Scrollen und nach jeder Seite.
    // Früh nachladen seit 28.10.2026: kam das Ende erst ins Bild, zog es die Karte mit nach oben.
    let frame = 0;
    const pruefen = () => {
      frame = 0;
      if (loading || loadingMore || currentPage + 1 >= totalPages) return;
      if (sentinel.getBoundingClientRect().top > window.innerHeight + 2500) return;
      loadEvents(currentPage + 1, true, searchQuery, selectedBundesland, selectedDate, minKm, maxKm, selectedCity,
        radiusApplies ? effectiveLat! : undefined, radiusApplies ? effectiveLng! : undefined, radiusApplies ? selectedRadius! : undefined,
        selectedCategory || undefined, selectedEventTypes.join(',') || undefined);
    };
    const beimScrollen = () => { if (!frame) frame = requestAnimationFrame(pruefen); };
    pruefen();
    document.addEventListener('scroll', beimScrollen, { capture: true, passive: true });
    window.addEventListener('resize', beimScrollen);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', beimScrollen, { capture: true });
      window.removeEventListener('resize', beimScrollen);
    };
  // loadEvents absichtlich ausgelassen: die Funktion haengt an denselben Filtern, die hier schon
  // stehen. Sie zusaetzlich aufzufuehren wuerde die Pruefung bei jedem Rendern neu aufsetzen und
  // beim Nachladen eine zweite Seite anfordern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendsOnly, loading, loadingMore, currentPage, totalPages, searchQuery, selectedBundesland, selectedCountry, selectedDate, selectedDateTo, minKm, maxKm, selectedCity, effectiveLat, effectiveLng, selectedRadius, radiusApplies, selectedCategory, selectedEventTypes, permanentView, showOffSeason]);

  const filteredEvents = useMemo(() => eventsOwner === user?.id ? events : [], [events, eventsOwner, user?.id]);

  /**
   * Listenzustand vor dem Öffnen eines Events sichern — nur beim normalen Klick: bei einem neuen
   * Tab bleibt die Liste ja stehen und darf nicht überschrieben werden. Steht als eine Funktion
   * hier, weil beide Card-Bauformen (normal und Bühne) denselben Zustand sichern müssen.
   */
  const rememberListState = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    saveScrollState({ eventCount: events.length });
    const hasRadius = radiusApplies;
    setLandingCache({
      key: JSON.stringify({ seite: SEITE, friendsOnly, userId: user?.id,
        searchQuery, selectedBundesland, selectedDate, dateTo: selectedDateTo, minKm, maxKm, selectedCity, permanentView, showOffSeason,
        lat: hasRadius ? effectiveLat : null, lng: hasRadius ? effectiveLng : null,
        radius: hasRadius ? selectedRadius : null,
        category: selectedCategory, eventType: selectedEventTypes.join(','),
      }),
      events, currentPage, totalPages, totalElements,
    });
  };

  /**
   * Welche Karten als Bühne (Hervorhebung 1b) gezeichnet werden dürfen.
   *
   * Die Stufe verlässt als einzige das Raster, deshalb darf sie sich nicht häufen: zwei doppelt
   * breite Cards nebeneinander zerlegen die Liste in Streifen und die Stufe verliert genau die
   * Wirkung, für die bezahlt wurde. Erlaubt ist eine je Ergebnisseite — die Liste lädt beim
   * Scrollen nach, gemessen wird deshalb der Abstand in Karten (`STAGE_MIN_GAP`, eine Seite der
   * Liste) und nicht die Seitenzahl. Weitere gebuchte Bühnen bleiben an ihrer Stelle und
   * erscheinen als Signatur-Rahmen; sie verschwinden nicht, sie treten nur nicht doppelt auf.
   */
  const stageIndexes = useMemo(() => {
    const allowed = new Set<number>();
    let last = -Infinity;
    filteredEvents.forEach((event, i) => {
      if (!isStageCard(event) || i - last < STAGE_MIN_GAP) return;
      allowed.add(i);
      last = i;
    });
    return allowed;
  }, [filteredEvents]);

  const handleFavorite = async (event: CyclingEvent) => {
    if (!isAuthenticated) {
      // Klick parken — wird nach Login/Registrierung automatisch als Favorit eingelöst.
      rememberPendingFavorite(event.id);
      navigate('/login', { state: { from: { pathname: '/' } } });
      return;
    }
    try {
      if (event.favorited) {
        await eventService.removeFavorite(event.id);
      } else {
        await eventService.addFavorite(event.id);
      }
      umamiTrack('event-favorit', { id: event.id, titel: event.title, aktion: event.favorited ? 'entfernt' : 'hinzugefuegt' });
      setEvents(prev => prev.map(e =>
        e.id === event.id ? { ...e, favorited: !e.favorited } : e
      ));
      // Die Karte führt ihre eigene Menge — ohne das trüge sie beim nächsten Öffnen noch das
      // Herz eines gerade entfernten Favoriten.
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (event.favorited) next.delete(event.id); else next.add(event.id);
        return next;
      });
      // Drop the cached snapshots so the old favorited state can't be restored: 'landing' on a
      // hard refresh, 'favorites' on the Merkliste. Both then refetch the true state.
      setLandingCache(null);
      clearListCache('favorites');
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const resetOrganizerState = () => {
    setOrganizerMode('freetext');
    setSelectedOrganizerId(null);
    setOrganizerSearch('');
    setNewOrganizerName('');
    setNewOrganizerContactPerson('');
    setNewOrganizerEmail('');
    setNewOrganizerPhone('');
    setNewOrganizerWebsite('');
  };

  const openProposeNew = () => {
    // Die Redaktion legt direkt an, statt sich selbst einen Vorschlag zur Prüfung zu schicken.
    if (isAdminOrModerator()) {
      navigate(`/events/manage?neu=1${permanentView ? '&permanent=1' : ''}`);
      return;
    }
    // Anonymous users may propose a new event, same as proposing an edit.
    // The backend endpoint (POST /api/v1/event-edits/propose) is public.
    setProposeEditEventId(null);
    // Aus der Liste „Permanent" heraus ist das Häkchen schon gesetzt — wer dort einträgt, meint
    // fast immer ein permanentes Event. Abwählen geht wie sonst auch.
    setProposeForm(permanentView ? { ...emptyProposeForm, permanent: true } : emptyProposeForm);
    resetOrganizerState();
    setProposeVersucht(false);
    setProposeServerFehler(null);
    setShowProposeModal(true);
  };

  // Open the propose-new modal when arriving via the header CTA from another page (`/?eintragen=1`).
  useEffect(() => {
    if (searchParams.get('eintragen') === '1') {
      openProposeNew();
      // Die Redaktion ist schon unterwegs ins Anlegen; ein Ersetzen der URL hier holte sie zurück.
      if (isAdminOrModerator()) return;
      const next = new URLSearchParams(searchParams);
      next.delete('eintragen');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const proposePruefEingabe = {
    ...proposeForm,
    newOrganizerName: organizerMode === 'new' ? newOrganizerName : null,
  };
  const proposeFehler = proposeVersucht ? pruefeEventVorschlag(proposePruefEingabe) : {};
  const proposeZusammenfassung = eventVorschlagZusammenfassung(proposeFehler);
  /** Feldklasse mit roter Umrandung, wenn das Feld einen Fehler hat. */
  const vfKlasse = (k: EventVorschlagFeld, extra = '') =>
    `vf-eingabe${extra ? ` ${extra}` : ''}${proposeFehler[k] ? ' vf-eingabe--fehler' : ''}`;
  const vfFehler = (k: EventVorschlagFeld) => proposeFehler[k]
    ? <p className="vf-fehler" id={`${PROPOSE_FELD_IDS[k]}-fehler`}><CircleAlert size={14} aria-hidden />{proposeFehler[k]}</p>
    : null;
  const vfAria = (k: EventVorschlagFeld) => proposeFehler[k]
    ? { 'aria-invalid': true as const, 'aria-describedby': `${PROPOSE_FELD_IDS[k]}-fehler` } : {};

  const handleProposeSave = async () => {
    // Fehler stehen am Feld (pruefeEventVorschlag, shared): markieren, zum ersten springen,
    // Cursor hinein. Vorher nur ein Toast oben rechts, das Feld musste man selbst suchen.
    const fehler = pruefeEventVorschlag(proposePruefEingabe);
    const erstes = Object.keys(fehler)[0] as EventVorschlagFeld | undefined;
    setProposeServerFehler(null);
    if (erstes) {
      setProposeVersucht(true);
      const el = document.getElementById(PROPOSE_FELD_IDS[erstes]);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus({ preventScroll: true }); }
      return;
    }
    setProposeSaving(true);
    try {
      const distances = proposeForm.distances.filter(d => d.distanceKm > 0 || (d.durationMinutes ?? 0) > 0);

      let organizerId: number | undefined = undefined;
      let organizerName: string | undefined = proposeForm.organizer || undefined;
      let newOrganizer: NewOrganizerData | undefined = undefined;

      if (organizerMode === 'existing') {
        organizerId = selectedOrganizerId ?? undefined;
        organizerName = undefined;
      } else if (organizerMode === 'new') {
        // Send the new organizer inline with the event proposal. It is NOT created live
        // (that needs admin rights and would pollute the catalog). When a moderator approves
        // the event proposal, the backend creates this organizer and links it to the event.
        newOrganizer = {
          name: newOrganizerName.trim(),
          contactPerson: newOrganizerContactPerson || undefined,
          email: newOrganizerEmail || undefined,
          phone: newOrganizerPhone || undefined,
          website: newOrganizerWebsite || undefined,
        };
        organizerName = undefined;
        organizerId = undefined;
      } else {
        organizerName = proposeForm.organizer || undefined;
        organizerId = undefined;
      }

      const req = {
        ...proposeForm,
        seasonFrom: proposeForm.permanent ? proposeForm.seasonFrom : null,
        seasonUntil: proposeForm.permanent ? proposeForm.seasonUntil : null,
        areaName: proposeForm.permanent ? (proposeForm.areaName.trim() || null) : null,
        endDate: proposeForm.endDate || undefined,
        eventType: proposeForm.eventType || undefined,
        locationName: proposeForm.locationName || undefined,
        street: proposeForm.street || undefined,
        houseNumber: proposeForm.houseNumber || undefined,
        distances: distances.map(d => ({ ...d, routeUrl: d.routeUrl || undefined, durationMinutes: d.durationMinutes && d.durationMinutes > 0 ? d.durationMinutes : undefined })),
        links: cleanLinks(proposeForm.links),
        organizerUrl: proposeForm.organizerUrl || undefined,
        description: proposeForm.description || undefined,
        organizer: organizerName,
        organizerId,
        newOrganizer,
        submitterName: proposeForm.submitterName.trim() || undefined,
        submitterEmail: proposeForm.submitterEmail.trim() || undefined,
      };

      if (proposeEditEventId) {
        await eventService.proposeEdit(proposeEditEventId, req);
      } else {
        await eventService.proposeNewEvent(req);
      }
      umamiTrack('event-vorschlag', { typ: proposeEditEventId ? 'aenderung' : 'neu' });
      toast.success('Dein Vorschlag wurde eingereicht und wird geprüft');
      setShowProposeModal(false);
    } catch (err: any) {
      setProposeServerFehler(err.response?.data?.error || 'Einreichen hat nicht geklappt. Bitte versuch es gleich noch einmal.');
    } finally {
      setProposeSaving(false);
    }
  };

  const addProposeDistance = () => {
    setProposeForm(f => ({ ...f, distances: [...f.distances, { distanceKm: 0, elevationMeters: 0, routeUrl: '' }] }));
  };

  const removeProposeDistance = (idx: number) => {
    setProposeForm(f => ({ ...f, distances: f.distances.filter((_, i) => i !== idx) }));
  };

  const updateProposeDistance = (idx: number, field: keyof DistanceRequest, value: number | string) => {
    setProposeForm(f => ({
      ...f,
      distances: f.distances.map((d, i) => i === idx ? { ...d, [field]: value } : d)
    }));
  };

  // Für die eingebetteten Bausteine (Anmeldung, Links), die ihre Felder per Style bekommen;
  // dieselbe Form wie `.vf-eingabe` in App.css.
  const proposeInputStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #E5E1E1',
    borderRadius: 10,
    color: '#2C2C2C',
    fontSize: 15,
    minHeight: 44,
    boxSizing: 'border-box',
    minWidth: 0,
  };
  const proposeLabelStyle: React.CSSProperties = { color: '#4A4A4A', fontSize: 13, fontWeight: 600, fontFamily: "'Hanken Grotesk', sans-serif" };
  const currentYear = new Date().getFullYear();
  const catHeadingLabel = selectedCategory
    ? (selectedCategory === 'ROAD' ? 'Rennrad & RTF' : selectedCategory === 'GRAVEL' ? 'Gravel' : 'MTB')
    : null;
  // Die H1 im Hero bleibt kurz („Rennrad Events"), das RTF-Keyword trägt der SEO-Title.
  const catHeroLabel = selectedCategory === 'ROAD' ? 'Rennrad' : catHeadingLabel;
  // Event-type landing page (e.g. /radmarathon) takes priority over discipline labels
  const eventTypeLanding = findEventTypeLandingByTypes(forcedEventTypes);
  // SEO-Titel: Deutschland bleibt Default (Hauptmarkt/Ranking), AT/CH nur bei entsprechender Wahl.
  // titleCountry ist die Ortsangabe für „… in {…}" (die Schweiz braucht den Artikel).
  const titleCountry = selectedCountry === 'AT' ? 'Österreich' : selectedCountry === 'CH' ? 'der Schweiz' : 'Deutschland';
  // „in ganz {Land}" — für die Schweiz „in der ganzen Schweiz".
  const titleCountryWide = selectedCountry === 'CH' ? 'der ganzen Schweiz' : `ganz ${titleCountry}`;
  const blDisp = (bl: string) => displayBundesland(bl);
  const siteTitle = (() => {
    const loc = selectedCity || (selectedBundesland ? blDisp(selectedBundesland) : null);
    if (permanentView) return PERMANENT_LANDING.title;
    if (eventTypeLanding) {
      const where = loc ? `in ${loc}` : `in ${titleCountry}`;
      return `${eventTypeLanding.label} ${where} ${currentYear} – Termine & Strecken | Radsport Events`;
    }
    if (catHeadingLabel && loc) return `${catHeadingLabel} Events in ${loc} ${currentYear} | Radsport Events`;
    if (catHeadingLabel) return `${catHeadingLabel} Events in ${titleCountry} ${currentYear} | Radsport Events`;
    if (selectedCity) return `Rennrad, RTF & Gravel Events in ${selectedCity} ${currentYear} | Radsport Events`;
    if (selectedBundesland) return `Rennrad, RTF & Gravel Events in ${blDisp(selectedBundesland)} ${currentYear} | Radsport Events`;
    return `Radsport Events – RTF, Rennrad & Gravel Events in ${titleCountry} ${currentYear}`;
  })();
  const siteDescription = (() => {
    const loc = selectedCity ? ` in ${selectedCity}` : selectedBundesland ? ` in ${blDisp(selectedBundesland)}` : ` in ${titleCountryWide}`;
    if (permanentView) return PERMANENT_LANDING.description;
    if (eventTypeLanding) {
      return `Alle ${eventTypeLanding.introNoun}${loc} ${currentYear} – Termine, Strecken und Anmeldungen auf einen Blick.`;
    }
    const cat = selectedCategory === 'ROAD'
      ? 'Rennrad- und RTF (Radtourenfahrt)'
      : selectedCategory === 'GRAVEL' ? 'Gravel-Bike'
      : selectedCategory === 'MTB' ? 'MTB'
      : 'Rennrad-, RTF- und Gravel-Bike';
    return `${cat}-Events${loc} – alle Termine, Strecken und Anmeldungen auf einen Blick. Jedermann-Rennen, Radmarathons und Radveranstaltungen entdecken.`;
  })();

  const landingContent = getLandingContent({
    category: selectedCategory,
    eventTypeLabel: eventTypeLanding?.label,
    bundesland: selectedBundesland ? displayBundesland(selectedBundesland) : undefined,
    city: selectedCity || undefined,
    year: currentYear,
  });
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: landingContent.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const itemListSchema = events.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: siteTitle,
    description: siteDescription,
    numberOfItems: totalElements,
    itemListElement: events.slice(0, 10).map((ev, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      // Permanent: kein Termin, also kein Event mit startDate (sonst der Platzhalter 1970).
      item: ev.permanent ? {
        '@type': 'SportsActivityLocation',
        name: ev.title,
        address: { '@type': 'PostalAddress', addressLocality: ev.city, addressCountry: 'DE' },
        url: `${window.location.origin}/events/${ev.id}`,
      } : {
        '@type': 'Event',
        name: ev.title,
        startDate: ev.eventDate,
        location: { '@type': 'Place', name: ev.locationName ?? ev.city, address: { '@type': 'PostalAddress', addressLocality: ev.city, addressCountry: 'DE' } },
        url: `${window.location.origin}/events/${ev.id}`,
      },
    })),
  } : null;

  // --- Redesign helpers ---------------------------------------------------
  const catColorOf = (c: EventCategory) => CATEGORY_COLORS[c] || '#922C57';
  /** Pille der schnellen Filter über der Liste (Desktop). `filled`: gewählt = dunkle Fläche wie die Kategorie-Pillen auf dem Handy. */
  const quickPill = (active: boolean, filled = false): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', flexShrink: 0,
    fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap',
    background: active && filled ? '#2C2C2C' : '#fff', color: active && filled ? '#fff' : '#2C2C2C',
    border: `1px solid ${active ? (filled ? '#2C2C2C' : '#922C57') : '#E5E1E1'}`,
  });
  const blDisplay = (bl: string) => displayBundesland(bl);

  const canonBl = (s: string) => s.trim().toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
  // Banner-Land = das explizit gewählte Land; '' = keins gewählt (gemischt) → kein Länder-Wort
  // im Banner. Steuert Eyebrow, Überschrift, Untertitel und die Bundesländer-Kachel.
  const bannerCountry: '' | CountryCode = selectedCountry;
  const bannerCountryLabel = bannerCountry ? COUNTRY_LABELS[bannerCountry] : '';       // bare, z. B. Eyebrow „SCHWEIZ"
  const bannerCountryLoc = bannerCountry === 'CH' ? 'der Schweiz' : bannerCountryLabel; // „… in der Schweiz"
  /**
   * Was die Liste ist, sichtbar über ihr — nur auf den Seiten, auf die Google führt (Disziplin,
   * Eventart, Region, Land), nicht auf der Startseite. Dort landen rund 40.000 Sitzungen, die meist
   * nichts mehr ändern; ohne die Zeile sah man auf `/rtf-events/Nordrhein-Westfalen` weder „RTF“
   * noch, am Handy, die Region. Die H1 bleibt unsichtbar und unverändert.
   */
  const listenTitel = (() => {
    if (pfad === '/' || permanentView) return null;
    const ort = selectedCity || (selectedBundesland ? blDisplay(selectedBundesland) : '') || (bannerCountry ? (bannerCountry === 'CH' ? 'der Schweiz' : COUNTRY_LABELS[bannerCountry]) : '');
    const was = eventTypeLanding?.label
      ?? (pfad.startsWith('/rtf-events') ? 'RTF'
        : forcedCategory ? `${forcedCategory === 'ROAD' ? 'Rennrad' : CATEGORY_LABELS[forcedCategory as EventCategory]} Events` : 'Events');
    return ort ? `${was} in ${ort}` : was;
  })();
  // Distinkte Länder im Datenbestand (DE/AT, künftig CH) für die Banner-Kachel — Coverage-Stat
  // wie KATEGORIEN, unabhängig vom Filter. Fallback 2.

  // Weitere Länder im Filter: ausschliesslich die, in denen tatsaechlich Events liegen. Die volle
  // Laenderliste gehoert ins Anlege-Formular — ein Filter auf ein leeres Land waere eine Sackgasse.
  // Ist die Liste leer, entfaellt das Dropdown ganz und der Filter sieht aus wie bisher.
  const foreignCountries = Array.from(
    new Set(availableBundeslaender.map(countryOfBundesland).filter((c): c is CountryCode => !!c))
  ).filter(c => !CORE_COUNTRIES.includes(c as never))
   .sort((a, b) => COUNTRY_LABELS[a].localeCompare(COUNTRY_LABELS[b], 'de'));
  const foreignSelected = !!selectedCountry && !CORE_COUNTRIES.includes(selectedCountry as never);

  const DIST_PRESETS: { label: string; min: string; max: string }[] = [
    { label: '< 50 km', min: '', max: '50' },
    { label: '50–100 km', min: '50', max: '100' },
    { label: '100–150 km', min: '100', max: '150' },
    { label: '> 150 km', min: '150', max: '' },
  ];
  const isDistActive = (p: { min: string; max: string }) => minKmInput === p.min && maxKmInput === p.max && (p.min !== '' || p.max !== '');
  const toggleDist = (p: { min: string; max: string }) => {
    if (isDistActive(p)) { setMinKmInput(''); setMaxKmInput(''); }
    else { setMinKmInput(p.min); setMaxKmInput(p.max); }
  };
  const RADIUS_PRESETS = [25, 50, 100, 200];
  // Ein per Karte gesetzter Zwischenwert (z. B. 170 km) hängt sich als fünfter, aktiver Chip an —
  // sonst stünde der aktive Umkreis nirgends und ein Klick auf einen Preset wirkte wie ein Reset.
  const radiusChips = selectedRadius != null && !RADIUS_PRESETS.includes(selectedRadius)
    ? [...RADIUS_PRESETS, selectedRadius]
    : RADIUS_PRESETS;
  // Stellenzahl der PLZ haengt am Land (DE 5, AT/CH 4, Italien 5, Belgien 4 …). Laender ohne reine
  // Ziffern-PLZ (Niederlande, Polen, …) liefern null — dort entfaellt das Feld, dort fuehrt der
  // Weg ueber Ortsnamen und Karte.
  const plzDigits = postalCodeDigits(selectedCountry || 'DE');
  const plzLength = plzDigits ?? 5;
  const plzValid = plzDigits != null && userPlz.length === plzLength;
  // Die PLZ aus dem Profil trägt den Ortsnamen gleich mit („30657 Hannover“): dann steht im
  // Ortsfeld „Hannover · 100 km“ statt einer Zahl, die man erst als die eigene erkennen muss.
  const locationLabel = placeName || selectedCity || (plzValid ? ortZurPlz(user?.location, userPlz) : '');

  // Ort mit Umkreis, wie ihn Karte und Liste verwenden (null ohne Umkreis).
  const kartenOrt = radiusApplies && effectiveLat != null && effectiveLng != null && selectedRadius != null
    ? { lat: effectiveLat, lng: effectiveLng, radiusKm: selectedRadius, label: locationLabel || 'Dein Standort' }
    : null;
  /**
   * Handy: Karte im Vollbild hinter dem schwebenden Knopf „Karte“ (Vorbild DICE und Airbnb). Die Liste
   * bleibt der Einstieg, die Karte ist ein Blick auf dieselbe Auswahl. Bis 26.10.2026 stand sie als
   * Block „Wo fährst du?“ über der Liste und schob das erste Event aus dem ersten Bildschirm.
   */
  const [handyKarte, setHandyKarte] = useState(false);
  // Am Rechner steht die Karte immer rechts neben der Liste (`lp-karte`), ab 1100 px Breite.
  const showSeitenKarte = !isPhone;
  /**
   * Liste und Karte am Rechner (28.10.2026): das Verhältnis lässt sich am Teiler verschieben, und
   * die Karte lässt sich ausblenden. Beides merkt sich der Browser.
   */
  // „Nur Karte“ gab es bis zum 30.10.2026: am Laptop stand dann dieselbe Karte, nur ohne Zeilen
  // daneben. Wer sie gewählt hatte, landet wieder bei Liste und Karte.
  const [ansicht, setAnsicht] = useState<'beide' | 'liste'>(() =>
    localStorage.getItem('radevents.startAnsicht') === 'liste' ? 'liste' : 'beide');
  const [listenAnteil, setListenAnteil] = useState<number | null>(() => {
    const v = Number(localStorage.getItem('radevents.listenAnteil'));
    return v >= 0.2 && v <= 0.8 ? v : null;
  });
  useEffect(() => { localStorage.setItem('radevents.startAnsicht', ansicht); }, [ansicht]);
  const gridRef = useRef<HTMLDivElement>(null);
  const teilerZiehen = (e: React.PointerEvent<HTMLDivElement>) => {
    const grid = gridRef.current;
    if (!grid) return;
    e.preventDefault();
    const box = grid.getBoundingClientRect();
    if (teilerHinweis) teilerHinweisWeg();
    let anteil = listenAnteil;
    const bewegen = (ev: PointerEvent) => {
      anteil = Math.min(0.75, Math.max(0.22, (ev.clientX - box.left) / box.width));
      setListenAnteil(anteil);
    };
    const loslassen = () => {
      window.removeEventListener('pointermove', bewegen);
      window.removeEventListener('pointerup', loslassen);
      document.body.classList.remove('lp-teiler-zieht');
      if (anteil != null) localStorage.setItem('radevents.listenAnteil', String(anteil));
    };
    document.body.classList.add('lp-teiler-zieht');
    window.addEventListener('pointermove', bewegen);
    window.addEventListener('pointerup', loslassen);
  };
  const teilerZuruecksetzen = () => { setListenAnteil(null); localStorage.removeItem('radevents.listenAnteil'); };
  /**
   * Beim ersten Besuch am Rechner (29.10.2026): eine Sprechblase am Teiler, der Griff wippt kurz.
   * Sonst findet den Teiler nur, wer mit der Maus zufällig darüberfährt. Sie geht für immer, sobald
   * jemand zieht, sie schließt oder irgendwo anders hinklickt: es ist keine Kernfunktion, die jeder
   * verstanden haben muss (Phil, 29.10.2026). Wer gar nichts tut, sieht sie beim nächsten Mal wieder.
   */
  const [teilerHinweis, setTeilerHinweis] = useState(false);
  useEffect(() => {
    if (isPhone || ansicht !== 'beide' || listenAnteil != null) { setTeilerHinweis(false); return; }
    if (localStorage.getItem('radevents.teilerHinweis')) return;
    const t = window.setTimeout(() => setTeilerHinweis(true), 1200);
    return () => window.clearTimeout(t);
  // Nur beim Erscheinen und beim Umschalten der Ansicht, nicht bei jeder Bewegung des Teilers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone, ansicht]);
  const teilerHinweisWeg = () => { setTeilerHinweis(false); localStorage.setItem('radevents.teilerHinweis', 'gesehen'); };
  // Ein Klick irgendwo anders (Filter, Liste, Karte) nimmt den Hinweis ebenfalls für immer weg.
  // Der Teiler selbst und die Sprechblase regeln das selbst.
  useEffect(() => {
    if (!teilerHinweis) return;
    const woanders = (e: PointerEvent) => {
      const ziel = e.target as Element | null;
      if (ziel?.closest('.lp-teiler, .lp-teiler-hinweis')) return;
      teilerHinweisWeg();
    };
    document.addEventListener('pointerdown', woanders, true);
    return () => document.removeEventListener('pointerdown', woanders, true);
  }, [teilerHinweis]);
  useEffect(() => {
    if (!handyKarte && !showSeitenKarte) return;
    ensureMapPoints();
    loadFavoriteIds();
  // Beim Erscheinen der Karte und beim Wechsel der Liste; die Abrufe merken sich, was geladen ist.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handyKarte, showSeitenKarte, isAuthenticated, permanentView]);
  const kartenPoints = useMemo(() => filterMapPoints(friendEventIds ? mapPoints.filter(p => friendEventIds.has(p.id)) : mapPoints, {
    category: selectedCategory || null, date: selectedDate || null, dateTo: selectedDateTo || null,
    minKm: minKm ? Number(minKm) : null, maxKm: maxKm ? Number(maxKm) : null, eventTypes: selectedEventTypes,
    bundesland: selectedBundesland || null, country: selectedCountry || null, includeOffSeason: permanentView && showOffSeason,
  }), [friendEventIds, mapPoints, selectedCategory, selectedDate, selectedDateTo, minKm, maxKm, selectedEventTypes, selectedBundesland, selectedCountry, permanentView, showOffSeason]);

  // Maus über einer Card: ihr Pin leuchtet auf der Karte auf. Bewusst kein State der Seite: jede
  // Card unter dem Zeiger zeichnete sonst die ganze Liste und alle Pins neu, beim Scrollen also
  // Dutzende Male je Sekunde. Die Card selbst hebt `:hover` hervor.
  const hoverStore = useMemo(createHoverStore, []);
  const setHoverId = hoverStore.set;
  // Werbung läuft immer in der Liste mit (`PROMO_FIRST`, `PROMO_EVERY`), auch eine Ankündigung: über
  // den ersten Events nahm sie auf dem Handy fast den ganzen ersten Bildschirm ein, neben der Karte
  // stand sie zwischen Filtern und Liste. Was läuft, steuert weiter /admin/werbeplatz.
  const [ortet, setOrtet] = useState(false);
  /** Eigenen Standort als Ort setzen; `sonst` läuft, wenn der Browser ihn nicht herausgibt. */
  const standortHolen = (sonst: () => void) => {
    if (!navigator.geolocation || !window.isSecureContext) { sonst(); return; }
    setOrtet(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setOrtet(false);
        umamiTrack('in-meiner-naehe', { ergebnis: 'ok' });
        applyPickedLocation({
          name: 'Dein Standort', lat: pos.coords.latitude, lng: pos.coords.longitude, radiusKm: selectedRadius ?? 100,
          filters: { category: selectedCategory, date: selectedDate, minKm: minKmInput, maxKm: maxKmInput },
        });
      },
      () => { setOrtet(false); umamiTrack('in-meiner-naehe', { ergebnis: 'fehler' }); sonst(); },
      { timeout: 20000, maximumAge: 300000 },
    );
  };

  const chipsDisziplin = (
    <>
    {!forcedCategory && Object.entries(CATEGORY_LABELS).map(([key, label]) => {
      const active = selectedCategory === key;
      // Eventart mit zuruecksetzen — sie ist kategorieabhaengig (sonst z.B. MTB + RTF, null Treffer).
      return (
        <button key={key} type="button" aria-pressed={active} style={quickPill(active, true)}
          onClick={() => { setSelectedCategory(active ? '' : key); setSelectedEventTypes([]); setEaOpen(false); setEaQuery(''); }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, transform: 'rotate(45deg)', background: active ? '#fff' : catColorOf(key as EventCategory) }} />{label}
        </button>
      );
    })}
    </>
  );

  /**
   * Die Filterfelder einmal, gezeichnet zweimal: am Rechner als Felder bzw. Knöpfe über der Liste, auf
   * dem Handy als Knöpfe in einer wischbaren Reihe, deren Menüs als Blatt von unten aufgehen
   * (28.10.2026, Vorbild Google Drive und Apple Music). Damit gibt es den Knopf „Filter“ nirgends mehr.
   */
  const ortFeld = (pille: boolean) => (
    <>
      {/* Ort als kleines Menü statt Karten-Dialog: die Karte rechts zeigt den Umkreis schon. */}
      <OrtFeld pille={pille} treffer={totalElements} label={radiusApplies ? `Ort · ${selectedRadius} km` : 'Ort'} active={radiusApplies || !!selectedBundesland || !!selectedCountry}
        value={radiusApplies ? (locationLabel || 'Standort')
          : selectedBundesland ? blDisplay(selectedBundesland)
          : selectedCountry ? COUNTRY_LABELS[selectedCountry] : 'Überall'}
        hatUmkreis={radiusApplies} radius={selectedRadius} radien={RADIUS_PRESETS} ortet={ortet}
        onOeffnen={() => { if (!places) loadPlaces().then(setPlaces).catch(() => {}); }}
        vorschlaege={q => ({
          orte: places ? searchPlaces(places, q, 5) : [],
          regionen: ALL_REGIONS.filter(bl => canonBl(bl).includes(canonBl(q))).slice(0, 3).map(bl => ({ key: bl, label: blDisplay(bl) })),
        })}
        onOrt={selectPlace}
        onRegion={selectBundesland}
        onPlz={(plz, land) => {
          // Vierstellig (AT/CH): direkt verorten wie ein gewählter Ort. Über `userPlz` liefe die
          // Suche im Land des Länderfilters und setzte ihn dafür; das schlösse Events jenseits der Grenze aus.
          if (land) {
            api.get(`/geocode/plz/${plz}`, { params: { country: land } })
              .then(res => selectPlace({ name: `PLZ ${plz}`, state: '', lat: res.data.lat, lng: res.data.lng }))
              .catch(() => {});
            return;
          }
          markLocationOn(); setSelectedBundesland(''); setPlaceName(''); setUserPlz(plz); setSelectedRadius(r => (r == null ? 100 : r));
        }}
        laender={CORE_COUNTRIES.map(c => ({ key: c, label: COUNTRY_LABELS[c] }))}
        onLand={c => { clearLocation(); setSelectedBundesland(''); setSelectedCountry(c as CountryCode); }}
        // Ohne freigegebenen Standort öffnet auf dem Handy die Karte zur Wahl; am Rechner steht sie daneben.
        onNaehe={() => standortHolen(pille ? openMapPicker : () => {})}
        onRadius={km => { markLocationOn(); setSelectedRadius(km); }}
        onUeberall={() => { clearLocation(); setSelectedBundesland(''); setSelectedCountry(''); }} />
    </>
  );
  const zeitraumFeld = (pille: boolean) => (
    <>
      {/* Zeitraum, dazu „Jederzeit fahrbar“ für die permanenten Events: die nutzen wenige, deshalb
          kein eigener Umschalter mehr über der Liste, sondern eine Möglichkeit in diesem Feld. */}
      <SuchFeld pille={pille} knoepfe treffer={totalElements} icon={<CalendarDays size={18} />} label="Zeitraum" active={permanentView || !!(selectedDate || selectedDateTo)}
        // Kurz („12.11. – 20.11.“): das Feld bzw. der Knopf ist schmal, das Jahr steht im Kalender.
        value={permanentView ? 'Jederzeit fahrbar' : (() => { const l = dateRangeLabel(selectedDate, selectedDateTo, formatDayMonth); return l ? l.charAt(0).toUpperCase() + l.slice(1) : 'Alle'; })()}
        selected={permanentView ? 'permanent' : isWeekendRange(selectedDate, selectedDateTo) ? 'we' : activeDaysPreset ? String(activeDaysPreset) : matchingMonth(selectedDate, selectedDateTo) ? `m:${matchingMonth(selectedDate, selectedDateTo)!.key}` : selectedDateTo ? 'eigen' : selectedDate ? 'ab' : ''}
        options={[
          { value: '', label: 'Alle Termine' },
          { value: 'we', label: 'Dieses Wochenende' },
          ...NEXT_DAYS_PRESETS.map(d => ({ value: String(d), label: `Nächste ${d} Tage` })),
          // Ganze Monate (Vorbild Spotify): in den Daten tauchen Spannen von 31, 61 und 92 Tagen auf.
          ...upcomingMonths(3).map(mo => ({ value: `m:${mo.key}`, label: mo.label })),
          { value: 'permanent', label: 'Jederzeit fahrbar' },
        ]}
        onClear={() => { if (permanentView) navigate({ pathname: '/', search: window.location.search }); setSelectedDate(''); setDateInput(''); setSelectedDateTo(''); }}
        // Kalender statt zweier Datumsfelder: der erste Tipp setzt „ab“ (häufigste Zeitraum-Nutzung),
        // der zweite „bis“.
        extra={permanentView ? undefined : () => (
          <ZeitraumKalender von={selectedDate} bis={selectedDateTo}
            onWechsel={(von, bis) => { setSelectedDate(von); setDateInput(von); setSelectedDateTo(bis); }} />
        )}
        onSelect={v => {
          if (v === 'permanent') { if (!permanentView) navigate({ pathname: `/${PERMANENT_LANDING.slug}`, search: window.location.search }); return; }
          if (permanentView) { navigate({ pathname: '/', search: window.location.search }); if (v === '') return; }
          if (v === '') { setSelectedDate(''); setDateInput(''); setSelectedDateTo(''); return; }
          const r = v === 'we' ? weekendRange() : v.startsWith('m:') ? upcomingMonths(3).find(mo => `m:${mo.key}` === v)! : nextDaysRange(Number(v));
          setSelectedDate(r.from); setDateInput(r.from); setSelectedDateTo(r.to);
        }} />
    </>
  );
  // Eventart mit Suche und allen Arten; ohne Disziplin zuerst Ultracycling, Bikepacking, 24h.
  const eventartFeld = (
    <EventartFeld pille treffer={totalElements} kategorie={selectedCategory} gewaehlt={selectedEventTypes}
      onWechsel={arten => setSelectedEventTypes(arten as EventType[])} />
  );
  const distanzFeld = (
    <DistanzFeld treffer={totalElements} min={minKmInput} max={maxKmInput} spannen={DIST_PRESETS}
      onWechsel={(mi, ma) => { setMinKmInput(mi); setMaxKmInput(ma); }} />
  );

  const resetFilters = () => {
    setFriendsOnly(false);
    setSelectedCategory(''); setSelectedEventTypes([]); setEaOpen(false); setEaQuery(''); setSelectedBundesland('');
    setSelectedCountry(''); setSelectedDate(''); setDateInput(''); setSelectedDateTo(''); setMinKmInput(''); setMaxKmInput('');
    // Auch das Zuruecksetzen ist eine Abwahl des Standorts — ohne die Marke stuende der Umkreis
    // um zuhause beim naechsten Seitenaufbau wieder da.
    markLocationOff();
    setUserPlz(''); setSelectedRadius(null); setSearchInput(''); setSearchQuery('');
    setPlaceName(''); setUserLat(null); setUserLng(null); setSelectedCity(''); setPlaceKey('');
  };

  // Switch country: filter to it (deselect if already active → back to all/mixed) and drop a
  // Bundesland that doesn't belong to the newly picked country.
  const pickCountry = (c: CountryCode) => {
    setSelectedCountry(prev => {
      const next = prev === c ? '' : c;
      // Bundesland verwerfen, wenn es nicht zum neuen Land gehört — inkl. Abwahl (next=''),
      // sonst filterte ein verstecktes Bundesland weiter.
      if (selectedBundesland && (!next || countryOfBundesland(selectedBundesland) !== next)) setSelectedBundesland('');
      return next;
    });
  };

  // `inSheet` = der Filter wird im mobilen Filter-Sheet bedient. Ort, Umkreis und Suchtext werden
  // es nicht: sie haben auf Phones ihre eigene Anzeige (Suchfeld bzw. Ort-Hinweis unter der
  // Suchzeile). Sie in die Badge am Filter-Button zu zählen, schickt Nutzer ins Sheet, wo sie den
  // gezählten Filter gar nicht finden — ein gesetzter Ort ergäbe dort eine „2" ohne Entsprechung.
  // paused: der Filter ist gesetzt, wirkt aber gerade nicht (Umkreis waehrend einer Textsuche).
  // `quick`: steht am Desktop schon als eigene Pille über der Liste und braucht keinen zweiten Chip.
  const activeFilters: { label: string; clear: () => void; inSheet: boolean; paused?: boolean; quick?: boolean }[] = [];
  // Kategorie loeschen raeumt die kategorieabhaengige Eventart gleich mit weg (wie die Chips).
  if (selectedCategory) activeFilters.push({ label: categoryLabel(selectedCategory as EventCategory), clear: () => { setSelectedCategory(''); setSelectedEventTypes([]); }, inSheet: true, quick: true });
  selectedEventTypes.forEach(t => activeFilters.push({ label: EVENT_TYPE_LABELS[t] ?? t, clear: () => setSelectedEventTypes(prev => prev.filter(x => x !== t)), inSheet: true, quick: showSeitenKarte }));
  // Land-Chip nur zeigen, wenn er eigene Information traegt — neben "Niedersachsen" waere "DE" redundant.
  if (selectedCountry && countryOfBundesland(selectedBundesland) !== selectedCountry) {
    // DE/AT/CH bleiben als Kuerzel stehen (so heissen auch die Chips); alles andere ausgeschrieben.
    activeFilters.push({ label: foreignSelected ? COUNTRY_LABELS[selectedCountry] : selectedCountry, clear: () => setSelectedCountry(''), inSheet: true, quick: showSeitenKarte });
  }
  // Am Rechner steht die Region im Feld „Ort“, dort ohne zweiten Chip.
  if (selectedBundesland) activeFilters.push({ label: blDisplay(selectedBundesland), clear: () => setSelectedBundesland(''), inSheet: true, quick: showSeitenKarte });
  // Schnellwahl „nächste X Tage": setzt ab = heute, bis = heute + X; ein zweiter Klick hebt sie auf.
  const activeDaysPreset = matchingNextDaysPreset(selectedDate, selectedDateTo);
  const toggleDaysPreset = (days: number) => {
    if (activeDaysPreset === days) {
      setSelectedDate(''); setDateInput(''); setSelectedDateTo('');
      return;
    }
    const r = nextDaysRange(days);
    setSelectedDate(r.from); setDateInput(r.from); setSelectedDateTo(r.to);
  };
  const dateFieldStyle: React.CSSProperties = { width: '100%', background: '#F5F3F3', border: '1px solid #E5E1E1', borderRadius: 10, padding: '10px 12px', fontWeight: 600, fontSize: 14, color: '#2C2C2C', boxSizing: 'border-box' };
  const dateChipLabel = dateRangeLabel(selectedDate, selectedDateTo, formatDayLong);
  if (dateChipLabel) activeFilters.push({ label: dateChipLabel, clear: () => { setSelectedDate(''); setDateInput(''); setSelectedDateTo(''); }, inSheet: true,
    // Steht als Pille in der Leiste über Liste und Karte (Rechner), braucht dort keinen zweiten Chip.
    // Am Rechner steht jeder Zeitraum im Feld „Zeitraum“, dort zählt er nicht noch einmal am Filter-Knopf.
    quick: showSeitenKarte || isWeekendRange(selectedDate, selectedDateTo) || activeDaysPreset === 30 });
  if (minKmInput || maxKmInput) activeFilters.push({ label: `${minKmInput || '0'}–${maxKmInput || '∞'} km`, clear: () => { setMinKmInput(''); setMaxKmInput(''); }, inSheet: true, quick: showSeitenKarte });
  if (placeName) activeFilters.push({ label: placeName, clear: clearLocation, inSheet: false, paused: radiusPaused });
  if (userPlz) activeFilters.push({ label: `PLZ ${userPlz}`, clear: () => setUserPlz(''), inSheet: false, paused: radiusPaused });
  // Der Umkreis-Chip zaehlt als Abwahl des Standorts: eine PLZ ohne Weite filtert nichts, sie
  // brachte beim naechsten Aufbau nur den 100-km-Vorschlag zurueck.
  if (selectedRadius != null) activeFilters.push({ label: `${selectedRadius} km Umkreis`, clear: () => { markLocationOff(); setSelectedRadius(null); }, inSheet: false, paused: radiusPaused });
  if (friendsOnly) activeFilters.push({ label: 'Mit Freunden', clear: () => setFriendsOnly(false), inSheet: false, quick: true });
  if (searchQuery) activeFilters.push({ label: `„${searchQuery}"`, clear: () => { setSearchInput(''); setSearchQuery(''); }, inSheet: false });
  /** Zählt die Badge am Filter-Button — nur was hinter dem Button auch wirklich steht. */
  // Am Rechner nicht mitzählen, was schon in der Leiste darüber steht (Disziplin, Zeitraum, Region).

  const monoLabel: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 11, letterSpacing: '0.12em', color: '#9A9494' };

  // ── Combined mobile search: free text · 5-digit PLZ · place from autocomplete ──
  const searchTrimmed = searchInput.trim();
  const isPlzQuery = /^\d{5}$/.test(searchTrimmed);
  const onCombinedSearch = (v: string) => {
    setSearchInput(v);
    setActiveSuggest(-1); // neue Eingabe → Tastaturauswahl verwerfen
    if (/^\d{5}$/.test(v.trim())) {
      setPlaceName('');
      setUserPlz(v.trim());
      setSelectedRadius(r => (r == null ? 100 : r));
    }
  };
  // Das "x" im Suchfeld muss auch den Ort mitnehmen, den die Eingabe gesetzt hat: eine getippte
  // PLZ landet nicht im Suchtext, sondern in userPlz + Radius. Nur den Text zu leeren liesse den
  // Umkreisfilter unsichtbar aktiv — die Liste bliebe gefiltert, das Suchfeld sagt "leer".
  const clearSearchBox = () => {
    if (isPlzQuery) setUserPlz(''); // der PLZ-Effekt raeumt lat/lng/radius hinterher
    setSearchInput('');
    setSearchQuery('');
  };
  const placeSuggest = (searchFocus && !isPlzQuery && searchTrimmed.length >= 2 && places)
    ? searchPlaces(places, searchTrimmed, 5)
    : [];
  const suggestOpen = searchFocus && !isPlzQuery && searchTrimmed.length >= 2;
  const selectPlace = (p: Place) => {
    // Place picked → use its coordinate as the radius origin (not a hard city-name filter).
    markLocationOn();
    setSelectedBundesland('');
    setPlaceName(p.name);
    plzClearedByPlaceRef.current = true;
    setUserPlz('');
    setUserLat(p.lat); setUserLng(p.lng);
    setSelectedRadius(r => (r == null ? 100 : r));
    // Wie bei der PLZ: die Ladeschleife sieht die Koordinaten nicht, nur diesen Schlüssel.
    setPlaceKey(`${p.lat},${p.lng}`);
    setSearchInput(''); setSearchQuery('');
    setSearchFocus(false);
  };
  // Bundesland suggestions: match the (umlaut-normalized) query against the canonical state list.
  const bundeslandSuggest = (searchFocus && !isPlzQuery && searchTrimmed.length >= 2)
    ? ALL_REGIONS.filter(bl => canonBl(bl).includes(canonBl(searchTrimmed)))
    : [];
  const selectBundesland = (bl: string) => {
    // Umkreis weg, wie bei der Wahl eines Landes: sonst gälten Bundesland und Umkreis um zuhause
    // zugleich, und Bayern mit 100 km um Hannover ergäbe eine fast leere Liste.
    clearLocation();
    setSelectedBundesland(bl);
    // Land mitziehen, sonst zeigt der Land-Chip weiter das alte/kein Land und das Bundesland-Dropdown
    // klappt weg bzw. listet die Regionen des falschen Landes.
    const c = countryOfBundesland(bl);
    if (c) setSelectedCountry(c);
    setSearchInput(''); setSearchQuery('');
    setSearchFocus(false);
  };
  // Flache Sicht auf alle Vorschlaege in Anzeige-Reihenfolge — Grundlage der Tastaturnavigation:
  // die Liste ist visuell in drei Gruppen geteilt, fuer Pfeiltasten/aria-activedescendant zaehlt
  // aber eine durchgehende Indizierung ueber alle Eintraege.
  const suggestOptions: Array<{ run: () => void }> = [
    ...bundeslandSuggest.map(bl => ({ run: () => selectBundesland(bl) })),
    ...placeSuggest.map(p => ({ run: () => selectPlace(p) })),
    ...eventSuggest.map(ev => ({ run: () => { setSearchFocus(false); navigate(`/events/${ev.id}`); } })),
  ];
  const blOffset = 0;
  const placeOffset = bundeslandSuggest.length;
  const eventOffset = placeOffset + placeSuggest.length;

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setSearchFocus(false); setActiveSuggest(-1); return; }
    if (!suggestOpen || suggestOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggest(i => (i + 1) % suggestOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggest(i => (i <= 0 ? suggestOptions.length : i) - 1);
    } else if (e.key === 'Enter' && activeSuggest >= 0 && activeSuggest < suggestOptions.length) {
      e.preventDefault();
      suggestOptions[activeSuggest].run();
      setActiveSuggest(-1);
    }
  };

  // Shared autocomplete dropdown (BUNDESLÄNDER + ORTE + EVENTS) — positioned by the caller.
  // idPrefix haelt die Options-IDs pro Instanz eindeutig (Desktop- und Phone-Layout rendern
  // dieselbe Liste), damit aria-activedescendant nicht auf ein doppeltes id-Attribut zeigt.
  const renderSuggestDropdown = (posStyle: React.CSSProperties, idPrefix: string) => {
    if (!suggestOpen || suggestOptions.length === 0) return null;
    const optionStyle = (idx: number): React.CSSProperties => ({
      display: 'flex', alignItems: 'center', gap: 11, padding: '10px 0', cursor: 'pointer',
      background: idx === activeSuggest ? 'rgba(146,44,87,0.06)' : 'transparent',
    });
    return (
      <div id={`${idPrefix}-listbox`} role="listbox" aria-label="Suchvorschläge" style={{ position: 'absolute', zIndex: 40, background: '#fff', borderRadius: 14, boxShadow: '0 16px 38px rgba(38,38,38,0.16)', maxHeight: 330, overflowY: 'auto', border: '1px solid #EDE9E9', ...posStyle }}>
        {bundeslandSuggest.length > 0 && (
          <div style={{ padding: '12px 14px 4px' }}>
            <div style={{ ...monoLabel, fontSize: 10, marginBottom: 2 }}>BUNDESLÄNDER</div>
            {bundeslandSuggest.map((bl, i) => (
              <div key={bl} id={`${idPrefix}-opt-${blOffset + i}`} role="option" aria-selected={activeSuggest === blOffset + i} onMouseDown={e => e.preventDefault()} onClick={() => selectBundesland(bl)} style={optionStyle(blOffset + i)}>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(146,44,87,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>🗺️</span>
                <span style={{ flex: 1, minWidth: 0, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 15, color: '#2C2C2C' }}>{blDisplay(bl)}</span>
                <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12, color: '#9A9494', flexShrink: 0 }}>Bundesland-Filter</span>
              </div>
            ))}
          </div>
        )}
        {placeSuggest.length > 0 && (
          <div style={{ padding: '12px 14px 4px', borderTop: bundeslandSuggest.length > 0 ? '1px solid #EDE9E9' : 'none' }}>
            <div style={{ ...monoLabel, fontSize: 10, marginBottom: 2 }}>ORTE</div>
            {placeSuggest.map((p, i) => (
              <div key={`${p.name}-${p.state}`} id={`${idPrefix}-opt-${placeOffset + i}`} role="option" aria-selected={activeSuggest === placeOffset + i} onMouseDown={e => e.preventDefault()} onClick={() => selectPlace(p)} style={optionStyle(placeOffset + i)}>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(146,44,87,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>📍</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 15, color: '#2C2C2C' }}>{p.name}</span>
                  {p.state && <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12, color: '#9A9494' }}> · {p.state}</span>}
                </span>
                <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12, color: '#9A9494', flexShrink: 0 }}>Umkreis-Suche</span>
              </div>
            ))}
          </div>
        )}
        {eventSuggest.length > 0 && (
          <div style={{ padding: '4px 14px 12px', borderTop: (placeSuggest.length > 0 || bundeslandSuggest.length > 0) ? '1px solid #EDE9E9' : 'none' }}>
            <div style={{ ...monoLabel, fontSize: 10, margin: '8px 0 2px' }}>EVENTS</div>
            {eventSuggest.map((ev, i) => {
              const color = catColorOf(ev.category);
              return (
                <div key={ev.id} id={`${idPrefix}-opt-${eventOffset + i}`} role="option" aria-selected={activeSuggest === eventOffset + i} onMouseDown={e => e.preventDefault()} onClick={() => { setSearchFocus(false); navigate(`/events/${ev.id}`); }} style={optionStyle(eventOffset + i)}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, transform: 'rotate(45deg)', background: color, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 15, color: '#2C2C2C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</span>
                    <span style={{ display: 'block', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12, color: '#9A9494' }}>{ev.city}, {blDisplay(ev.bundesland)}</span>
                  </span>
                  <span style={{ color: '#C9C4C4', fontSize: 18, flexShrink: 0 }}>›</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const canonicalUrl = window.location.origin + window.location.pathname;
  const breadcrumbSchema = (selectedBundesland || selectedCategory) ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Radsport Events', item: window.location.origin },
      ...(selectedCategory ? [{ '@type': 'ListItem', position: 2, name: selectedCategory === 'ROAD' ? 'Rennrad Events' : selectedCategory === 'GRAVEL' ? 'Gravel Events' : 'MTB Events', item: `${window.location.origin}/${selectedCategory === 'ROAD' ? 'rennrad' : selectedCategory === 'GRAVEL' ? 'gravel' : 'mtb'}-events` }] : []),
      ...(selectedBundesland ? [{ '@type': 'ListItem', position: selectedCategory ? 3 : 2, name: selectedBundesland, item: canonicalUrl }] : []),
    ],
  } : null;

  return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .lp-sidebar { position:sticky; top:20px; display:flex; flex-direction:column; gap:18px; max-height:calc(100vh - 40px); overflow-y:auto; overflow-x:hidden; overscroll-behavior:contain; scrollbar-width:thin; }
        .lp-sidebar > * { flex-shrink:0; }
        .lp-mobile-controls { display:none; }
        @media (max-width:900px) {
          .lp-sidebar { position:static; max-height:none; overflow:visible; }
        }
        /* App-like Entdecken layout on phones */
        @media (max-width:768px) {
          .lp-titel { display:none; }
          .lp-mobile-controls { display:flex; }
          .lp-sidebar {
            position:fixed; left:0; right:0; bottom:0; top:auto; z-index:110; margin:0;
            max-height:88vh; overflow-y:auto; overflow-x:hidden; overscroll-behavior:contain; background:#fff;
            border-radius:24px 24px 0 0; padding:12px 16px calc(20px + env(safe-area-inset-bottom,0px));
            box-shadow:0 -12px 40px rgba(44,44,44,0.22);
            transform:translateY(110%); transition:transform .28s cubic-bezier(.2,.8,.2,1);
            pointer-events:none;
          }
          .lp-sidebar.lp-sidebar--open { transform:none; pointer-events:auto; }
          .lp-filter-card { border:none !important; padding:0 !important; border-radius:0 !important; }
          .lp-sheet-backdrop { position:fixed; inset:0; background:rgba(44,44,44,0.5); z-index:105; }
        }
        /* Wischbare Chip-Reihe (Handy): läuft über den Rand hinaus statt umzubrechen. */
        .lp-chipreihe { display:flex; gap:8px; overflow-x:auto; scrollbar-width:none; margin:0 -16px; padding:0 16px; }
        .lp-chipreihe::-webkit-scrollbar { display:none; }
        .lp-chipreihe button:disabled { opacity:.7; cursor:wait; }
        /* Schwebender Knopf „Karte“/„Liste“ über der Tab-Leiste, wie bei DICE. */
        .lp-karte-fab {
          position:fixed; left:50%; transform:translateX(-50%); bottom:calc(84px + env(safe-area-inset-bottom,0px)); z-index:105;
          display:flex; align-items:center; gap:8px; height:46px; padding:0 20px; border:none; border-radius:999px;
          background:#2C2C2C; color:#fff; font-family:'Hanken Grotesk',sans-serif; font-weight:700; font-size:15px;
          box-shadow:0 4px 16px rgba(0,0,0,0.22); cursor:pointer;
        }
        .lp-handykarte { position:fixed; inset:0 0 calc(64px + env(safe-area-inset-bottom,0px)) 0; z-index:104; background:#E9EBE5; isolation:isolate; }
        /* Kopf der Handy-Karte: Filter-Reihe und Zahl. Über den Leaflet-Ebenen (z-index 400–700). */
        .lp-handykarte-kopf {
          position:absolute; top:0; left:0; right:0; z-index:1000; padding:calc(10px + env(safe-area-inset-top,0px)) 0 8px;
          background:linear-gradient(rgba(245,243,243,0.96) 70%, rgba(245,243,243,0)); display:grid; gap:8px; justify-items:center;
        }
        .lp-handykarte-kopf .lp-chipreihe { margin:0; width:100%; box-sizing:border-box; }
        .lp-handykarte-kopf .lp-chipreihe > button, .lp-handykarte-kopf .filter-pille { box-shadow:0 1px 4px rgba(0,0,0,0.12); }
        /* Ein offenes Filter-Blatt liegt über dem Knopf „Liste“. */
        .lp-handykarte-kopf:has(.blatt-hintergrund) { z-index:1100; }
        .lp-handykarte-zahl {
          background:#fff; border-radius:999px; padding:6px 14px; box-shadow:0 1px 6px rgba(0,0,0,0.2); white-space:nowrap;
          font-family:'Hanken Grotesk',sans-serif; font-weight:700; font-size:13px; color:#2C2C2C;
        }
        /* „Liste“ liegt in der Karte und muss über deren Ebenen. */
        .lp-karte-fab--zurueck { z-index:1000; }
        .lp-freunde-schalter {
          display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:12px; cursor:pointer;
          background:#fff; border:1px solid #E5E1E1; color:#2C2C2C; font-family:'Hanken Grotesk',sans-serif; font-weight:600; font-size:14px;
        }
        .lp-freunde-schalter svg { color:#922C57; }
        .lp-freunde-switch { margin-left:auto; width:36px; height:22px; border-radius:999px; background:#D9D3D3; position:relative; transition:background .15s; flex-shrink:0; }
        .lp-freunde-switch::after { content:''; position:absolute; top:3px; left:3px; width:16px; height:16px; border-radius:999px; background:#fff; transition:transform .15s; }
        .lp-freunde-switch--an { background:#922C57; }
        .lp-freunde-switch--an::after { transform:translateX(14px); }
        /* Am Rechner: Liste links, Karte rechts. Die Filter liegen in einer Schublade von links,
           die der Knopf „Filter“ öffnet. Ohne Platz für die Karte (unter 1100 px) bleibt die
           Liste allein. */
        @media (min-width:769px) {
          /* Breite der Liste: ohne eigene Wahl die Hälfte (30.10.2026, vorher 40 % und höchstens
             820 px), höchstens 1100 px; sonst \`--liste\` vom Teiler. Unter 1100 px gibt es keine Karte.
             Die Karte behält mindestens 360 px, die Liste 380 px. */
          .lp-grid--karte { grid-template-columns:clamp(380px, var(--liste, min(50%, 1100px)), calc(100% - 360px)) minmax(0,1fr); max-width:none; padding:0; gap:0; }
          .lp-grid--karte > div:not(.lp-karte) { padding:11px 28px 48px 32px; }
          .lp-grid--karte .lp-cards { grid-template-columns:1fr; }
          .lp-suchzeile { display:grid; gap:12px; }
          .lp-felder { display:grid; grid-auto-flow:column; grid-auto-columns:minmax(0,1fr); gap:8px; container-type:inline-size; }
          /* Schmale Liste (bis 1400 px Fenster): ohne Symbole, sonst kürzen die Werte („Alle T…“). */
          @container (max-width: 560px) {
            .lp-felder .such-feld { padding:8px 12px; gap:8px; min-height:52px; }
            .lp-felder .such-feld-icon { display:none; }
            .lp-felder .such-feld-wert { font-size:14px; }
          }
          .lp-schnell { display:flex; flex-wrap:wrap; align-items:center; gap:8px; }
          .lp-grid--karte .lp-sidebar {
            position:fixed; top:0; left:0; bottom:0; width:380px; max-width:90vw; max-height:none; z-index:110; margin:0;
            background:#F5F3F3; padding:16px; box-sizing:border-box; box-shadow:12px 0 40px rgba(44,44,44,0.18);
            transform:translateX(-105%); transition:transform .28s cubic-bezier(.2,.8,.2,1); pointer-events:none;
          }
          .lp-grid--karte .lp-sidebar.lp-sidebar--open { transform:none; pointer-events:auto; }
          .lp-sheet-backdrop { position:fixed; inset:0; background:rgba(44,44,44,0.35); z-index:105; }
          /* Die Karte reicht bis unten. */
          .lp-karte { position:sticky; top:var(--kopf-hoehe, 69px); height:calc(100vh - var(--kopf-hoehe, 69px)); overflow:hidden; border-left:1px solid #E5E1E1; isolation:isolate; }
        }
        @media (min-width:769px) and (max-width:1099px) {
          .lp-grid--karte { grid-template-columns:1fr; }
          .lp-karte { display:none; }
        }
      `}</style>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <meta name="keywords" content={`RTF, Radtourenfahrt, Rennrad Events, Gravel Events, Jedermann Rennen, Radmarathon, Fahrradveranstaltungen Deutschland${selectedBundesland ? `, ${selectedBundesland}` : ''}`} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {itemListSchema && <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>}
        {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Header — einheitlich auf allen Seiten. Der Filter-Button sitzt auf Phones nicht mehr
          hier, sondern in der Suchzeile darunter (neben Suchfeld und Ort-Pin). */}
      <PublicHeader onProposeEvent={openProposeNew} />
      {/* Nur für Konten, die einen Veranstalter pflegen — sonst rendert die Leiste nichts. */}
      <OrganizerShortcutBanner />



      {/* Ort & Umkreis auf der Karte — Vollbild auf Phones, Modal auf dem Desktop */}
      {mapOpen && (
        <LocationPicker
          variant={isPhone ? 'fullscreen' : 'modal'}
          onClose={() => setMapOpen(false)}
          onApply={applyPickedLocation}
          initialCenter={effectiveLat != null && effectiveLng != null ? { lat: effectiveLat, lng: effectiveLng } : null}
          initialRadiusKm={selectedRadius}
          initialName={locationLabel}
          initialFilters={{ category: selectedCategory, date: selectedDate, minKm: minKmInput, maxKm: maxKmInput }}
          lockedFilters={mapLockedFilters}
          eventTypes={selectedEventTypes}
          favoriteIds={favoriteIds}
          points={mapPoints}
          pointsLoading={mapPointsLoading}
          permanentPoints={permanentView ? undefined : permanentMapPoints}
          onShowPermanent={permanentView ? undefined : showPermanentNearby}
        />
      )}

      {/* Main 2-column layout */}
      {mobileFilterOpen && (
        <div className="lp-sheet-backdrop" onClick={() => setMobileFilterOpen(false)} />
      )}
      <div ref={gridRef} className={`lp-grid${showSeitenKarte ? ` lp-grid--karte lp-ansicht--${ansicht}` : ''}`}
        style={showSeitenKarte && listenAnteil != null ? { ['--liste' as string]: `${(listenAnteil * 100).toFixed(1)}%` } : undefined}>
        {/* Sidebar filters (becomes a bottom sheet on phones, a drawer next to the map on desktop) */}
        <aside ref={filterSheetRef} className={`lp-sidebar${mobileFilterOpen ? ' lp-sidebar--open' : ''}`}>
          {/* Mobile sheet grab handle — großzügige Touch-Fläche, per Drag nach unten schließbar */}
          <div
            className="hide-desktop"
            style={{ padding: '10px 0 14px', margin: '-12px -16px 0', touchAction: 'none', cursor: 'grab' }}
            onTouchStart={onSheetHandleTouchStart}
            onTouchMove={onSheetHandleTouchMove}
            onTouchEnd={onSheetHandleTouchEnd}
            onTouchCancel={onSheetHandleTouchEnd}
          >
            <div style={{ width: 38, height: 4, borderRadius: 2, background: '#E5E1E1', margin: '0 auto' }} />
          </div>
          <div className="lp-filter-card" style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 17, color: '#2C2C2C' }}>Filter</span>
              <span onClick={resetFilters} style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 13, color: '#922C57', cursor: 'pointer' }}>Zurücksetzen</span>
            </div>

            {/* Category tiles */}
            <div style={{ ...monoLabel, marginBottom: 8 }}>KATEGORIE</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 14 }}>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                const active = selectedCategory === key;
                const color = catColorOf(key as EventCategory);
                return (
                  <button
                    key={key}
                    onClick={() => { setSelectedCategory(active ? '' : key); setSelectedEventTypes([]); setEaOpen(false); setEaQuery(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 6px', borderRadius: 10,
                      cursor: 'pointer', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 13.5,
                      background: active ? '#2C2C2C' : '#F5F3F3',
                      color: active ? '#fff' : '#4A4A4A',
                      border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`,
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 3, transform: 'rotate(45deg)', background: active ? '#fff' : color, flexShrink: 0 }} />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Eventart — nur sichtbar, wenn eine Kategorie gewählt ist (spart Platz) */}
            {selectedCategory && (() => {
              const opts = sortEventTypesByLabel(EVENT_TYPES_BY_CATEGORY[selectedCategory] || []);
              const q = eaQuery.trim().toLowerCase();
              const shown = opts.filter(t => (EVENT_TYPE_LABELS[t] ?? t).toLowerCase().includes(q));
              const triggerLabel = selectedEventTypes.length === 0
                ? 'Alle Eventarten'
                : selectedEventTypes.length === 1
                  ? (EVENT_TYPE_LABELS[selectedEventTypes[0]] ?? selectedEventTypes[0])
                  : `${selectedEventTypes.length} ausgewählt`;
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ ...monoLabel, marginBottom: 8 }}>EVENTART</div>
                  <button
                    type="button"
                    onClick={() => setEaOpen(o => !o)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#F5F3F3', border: `1px solid ${eaOpen ? '#922C57' : '#E5E1E1'}`, borderRadius: 10, padding: '11px 12px', cursor: 'pointer', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 14, color: selectedEventTypes.length ? '#2C2C2C' : '#9A9494', boxSizing: 'border-box' }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{triggerLabel}</span>
                    <span style={{ fontFamily: "'Space Mono',monospace", color: '#9A9494', fontSize: 12, flexShrink: 0 }}>{eaOpen ? '▲' : '▾'}</span>
                  </button>
                  {eaOpen && (
                    <div style={{ marginTop: 8, border: '1px solid #E5E1E1', borderRadius: 12, background: '#fff', boxShadow: '0 14px 34px rgba(44,44,44,0.16)', padding: 10, maxHeight: 320, overflowY: 'auto' }}>
                      <input
                        autoFocus
                        value={eaQuery}
                        onChange={e => setEaQuery(e.target.value)}
                        placeholder="Eventart suchen…"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #E5E1E1', borderRadius: 8, padding: '9px 10px', fontSize: 13, fontFamily: "'Hanken Grotesk',sans-serif", marginBottom: 8, outline: 'none' }}
                      />
                      {shown.length === 0 ? (
                        <div style={{ color: '#9A9494', fontSize: 13, fontFamily: "'Hanken Grotesk',sans-serif", padding: '6px 4px' }}>Keine Treffer</div>
                      ) : shown.map(t => {
                        const on = selectedEventTypes.includes(t);
                        return (
                          <button
                            type="button"
                            key={t}
                            onClick={() => setSelectedEventTypes(prev => on ? prev.filter(x => x !== t) : [...prev, t])}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px', borderRadius: 8, cursor: 'pointer', border: 'none', background: on ? '#F7EEF2' : 'transparent', textAlign: 'left', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 13, color: '#2C2C2C' }}
                          >
                            <span style={{ width: 17, height: 17, flexShrink: 0, borderRadius: 5, border: `1px solid ${on ? '#922C57' : '#C4BFBF'}`, background: on ? '#922C57' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1 }}>{on ? '✓' : ''}</span>
                            {EVENT_TYPE_LABELS[t] ?? t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedEventTypes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
                      {selectedEventTypes.map(t => (
                        <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #D8D3D3', background: '#fff', borderRadius: 999, padding: '5px 10px', fontSize: 12, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, color: '#2C2C2C' }}>
                          {EVENT_TYPE_LABELS[t] ?? t}
                          <span onClick={() => setSelectedEventTypes(prev => prev.filter(x => x !== t))} style={{ cursor: 'pointer', color: '#9A9494' }}>✕</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Zeitraum: Schnellwahl „nächste X Tage" oder frei ab/bis — nicht bei permanenten Events */}
            {!permanentView && (<>
            <div style={{ ...monoLabel, marginBottom: 8 }}>ZEITRAUM</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
              {NEXT_DAYS_PRESETS.map(days => {
                const active = activeDaysPreset === days;
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => toggleDaysPreset(days)}
                    style={{
                      cursor: 'pointer', padding: '8px 12px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                      background: active ? '#2C2C2C' : '#F5F3F3',
                      color: active ? '#fff' : '#2C2C2C',
                      border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`,
                    }}
                  >
                    {days} Tage
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <label style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...monoLabel, display: 'block', fontSize: 10, marginBottom: 4 }}>AB</span>
                <input
                  type="date"
                  aria-label="Events ab Datum"
                  value={dateInput}
                  onChange={e => { setDateInput(e.target.value); setSelectedDate(e.target.value); }}
                  style={dateFieldStyle}
                />
              </label>
              <label style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...monoLabel, display: 'block', fontSize: 10, marginBottom: 4 }}>BIS</span>
                <input
                  type="date"
                  aria-label="Events bis Datum"
                  value={selectedDateTo}
                  min={dateInput || undefined}
                  onChange={e => setSelectedDateTo(e.target.value)}
                  style={dateFieldStyle}
                />
              </label>
            </div>
            </>)}

            {/* Distanz chips */}
            <div style={{ ...monoLabel, marginBottom: 8 }}>DISTANZ</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
              {DIST_PRESETS.map(p => {
                const active = isDistActive(p);
                return (
                  <button
                    key={p.label}
                    onClick={() => toggleDist(p)}
                    style={{
                      cursor: 'pointer', padding: '8px 12px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                      background: active ? '#2C2C2C' : '#F5F3F3',
                      color: active ? '#fff' : '#2C2C2C',
                      border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`,
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Land (Chips, abwählbar = gemischt) + Bundesland (Dropdown, kontextabhängig zum Land) */}
            <div style={{ ...monoLabel, marginBottom: 8 }}>LAND</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 12 }}>
              {CORE_COUNTRIES.map(code => {
                const active = selectedCountry === code;
                return (
                  <button
                    key={code}
                    onClick={() => pickCountry(code)}
                    title={COUNTRY_LABELS[code]}
                    style={{
                      cursor: 'pointer', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                      background: active ? 'rgba(146,44,87,0.08)' : '#F5F3F3',
                      color: active ? '#922C57' : '#2C2C2C',
                      border: `1px solid ${active ? '#922C57' : '#E5E1E1'}`,
                    }}
                  >
                    {code}
                  </button>
                );
              })}
              {/* Weitere Länder als Dropdown statt als Chips: ausgeschrieben (bei „MK" oder „BA"
                  raet sonst niemand richtig) und nur die, in denen Events liegen. Es sitzt bewusst
                  IN der Chip-Zeile: eine eigene Zeile hat das mobile Filter-Sheet ueber die
                  sichtbare Hoehe geschoben. Ohne solche Events erscheint es gar nicht, und steht
                  ein Chip auf DE/AT/CH, ist die Frage nach einem weiteren Land beantwortet. */}
              {foreignCountries.length > 0 && (!selectedCountry || foreignSelected) && (
                <select
                  value={foreignSelected ? selectedCountry : ''}
                  onChange={e => {
                    const next = e.target.value as '' | CountryCode;
                    setSelectedCountry(next);
                    // Region verwerfen: sie gehoert nie zum neu gewaehlten Land (und beim
                    // Zuruecksetzen wuerde sie sonst unsichtbar weiterfiltern).
                    setSelectedBundesland('');
                  }}
                  style={{ flex: '1 1 132px', minWidth: 0, background: '#F5F3F3', border: `1px solid ${foreignSelected ? '#922C57' : '#E5E1E1'}`, borderRadius: 8, padding: '8px 8px', fontWeight: 600, fontSize: 13, color: foreignSelected ? '#922C57' : '#2C2C2C' }}
                >
                  <option value="">Weitere Länder …</option>
                  {foreignCountries.map(c => (
                    <option key={c} value={c}>{COUNTRY_LABELS[c]}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Bundesland erst zeigen, wenn ein Land gewählt ist — dann nur dessen Regionen. Laender
                ohne eigene Regionsgliederung (Italien, Belgien …) haben nur einen Wert, der dem Chip
                entspricht: dort waere das Dropdown eine leere Geste. */}
            {selectedCountry && regionsOfCountry(selectedCountry).length > 1 && (
              <select
                value={selectedBundesland}
                onChange={e => setSelectedBundesland(e.target.value)}
                style={{ width: '100%', background: '#F5F3F3', border: '1px solid #E5E1E1', borderRadius: 10, padding: '11px 12px', fontWeight: 600, fontSize: 14, color: '#2C2C2C', marginBottom: 16 }}
              >
                <option value="">Alle Bundesländer</option>
                {/* Wert bleibt der interne ASCII-Schluessel, angezeigt wird die Umlaut-Schreibweise. */}
                {availableBundeslaender.filter(b => countryOfBundesland(b) === selectedCountry).map(b => (
                  <option key={b} value={b}>{blDisplay(b)}</option>
                ))}
              </select>
            )}

            {/* Standort (PLZ) + Umkreis — auf Phones bewusst nicht im Sheet: dort läuft der Ort
                ausschließlich über den Ort-Screen (Pin-Button in der Suchzeile). */}
            {!isPhone && (
              <>
                <div style={{ ...monoLabel, marginBottom: 8 }}>{plzDigits != null ? 'STANDORT (PLZ)' : 'STANDORT'}</div>
                {/* Laender ohne reine Ziffern-PLZ (Niederlande, Polen …) bekommen kein PLZ-Feld —
                    dort fuehrt der Weg ueber „Auf Karte wählen" weiter unten. */}
                {plzDigits != null && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5F3F3', border: `1px solid ${plzError ? '#A82F5A' : plzValid ? '#922C57' : '#E5E1E1'}`, borderRadius: 10, padding: '10px 12px', marginBottom: plzError ? 4 : 8 }}>
                      <span>📍</span>
                      <input
                        value={userPlz}
                        inputMode="numeric"
                        maxLength={plzLength}
                        onChange={e => setUserPlz(e.target.value.replace(/\D/g, ''))}
                        placeholder={selectedCountry === 'CH' ? 'z. B. 8001' : selectedCountry === 'AT' ? 'z. B. 6020' : selectedCountry && selectedCountry !== 'DE' ? `${plzLength} Ziffern` : 'z. B. 30657'}
                        style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 14, color: '#2C2C2C' }}
                      />
                      {plzGeocoding && <span style={{ fontSize: 12, color: '#9A9494' }}>…</span>}
                      {!plzGeocoding && plzValid && effectiveLat != null && !plzError && <span style={{ fontSize: 13, fontWeight: 700, color: '#922C57' }}>✓</span>}
                    </div>
                    {plzError && <div style={{ fontSize: 12, color: '#A82F5A', marginBottom: 8 }}>PLZ nicht gefunden</div>}
                  </>
                )}
                {/* Umkreis erst zeigen, wenn tatsächlich eine PLZ eingegeben (oder ein Ort gewählt)
                    wurde — nicht schon bei den Profil-Koordinaten eingeloggter Nutzer. Spart Platz. */}
                {(plzValid || !!placeName) && effectiveLat != null && effectiveLng != null && (
                  <>
                    <div style={{ ...monoLabel, fontSize: 10, margin: '10px 0 8px' }}>UMKREIS (KM)</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {radiusChips.map(r => {
                        const active = selectedRadius === r;
                        return (
                          <button
                            key={r}
                            onClick={() => {
                              // Die aktive Weite abzuwaehlen heisst „ohne Umkreis" — und das soll
                              // den Seitenwechsel ueberleben (siehe utils/locationFilter).
                              if (active) markLocationOff(); else markLocationOn();
                              setSelectedRadius(active ? null : r);
                            }}
                            style={{
                              flex: 1, minWidth: 0, cursor: 'pointer', padding: '8px 2px', borderRadius: 8, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
                              background: active ? '#922C57' : '#F5F3F3',
                              color: active ? '#fff' : '#2C2C2C',
                              border: `1px solid ${active ? '#922C57' : '#E5E1E1'}`,
                            }}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                    {radiusPaused && (
                      <div style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12, color: '#9A9494', marginTop: 8 }}>
                        Zählt gerade nicht — die Textsuche sucht überall.
                      </div>
                    )}
                  </>
                )}
                {/* Ort-Hinweis — eine Zeile, kürzt mit Ellipse */}
                {locationLabel && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 12.5, color: '#6A6A6A', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <span style={{ width: 7, height: 7, background: '#922C57', transform: 'rotate(45deg)', borderRadius: 2, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                      <strong style={{ color: '#2C2C2C' }}>{locationLabel}</strong>{selectedRadius != null ? ` · ${selectedRadius} km` : ''}
                    </span>
                    <span onClick={clearLocation} style={{ color: '#922C57', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>zurücksetzen</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={openMapPicker}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 13, color: '#922C57' }}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#922C57" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" />
                  </svg>
                  Auf Karte wählen
                </button>
              </>
            )}
          </div>

          {/* Filter übernehmen und Blatt bzw. Schublade schließen */}
          <button
            onClick={() => setMobileFilterOpen(false)}
            style={{ width: '100%', marginTop: 16, background: '#922C57', color: '#fff', border: 'none', padding: 15, borderRadius: 12, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
          >
            {totalElements} Events anzeigen
          </button>

        </aside>

        {/* Right column */}
        <div className="lp-liste" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Mobile app-style controls (phones only) — matches the App-Prototyp Events list */}
          <div className="lp-mobile-controls" style={{ flexDirection: 'column', gap: 12, paddingTop: 16 }}>
            <TabPageHeading>{permanentView ? "Permanente Events" : "Events"}</TabPageHeading>
            {/* Suchzeile: Freitext · PLZ · Ort — mit Pin-Button (Ort-Screen) im Feld und
                Filter-Button daneben. Umkreis und Standort laufen hier ausschließlich über den
                Ort-Screen, deshalb keine Umkreis-Chip-Zeile mehr unter der Suche. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div ref={mobileSearchRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: '#fff', border: `1px solid ${suggestOpen ? '#922C57' : '#E5E1E1'}`, borderRadius: 14, padding: '9px 10px 9px 15px', boxShadow: '0 3px 12px rgba(38,38,38,0.05)' }}>
                  <span style={{ color: '#9A9494', fontSize: 17, lineHeight: 1 }}>⌕</span>
                  <input
                    value={searchInput}
                    onChange={e => onCombinedSearch(e.target.value)}
                    onFocus={focusSearch}
                    onKeyDown={onSearchKeyDown}
                    placeholder="Event, Ort oder PLZ suchen…"
                    aria-label="Suche nach Event, Ort, PLZ oder Bundesland"
                    role="combobox"
                    aria-expanded={suggestOpen && suggestOptions.length > 0}
                    aria-controls="lp-mobile-listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={activeSuggest >= 0 ? `lp-mobile-opt-${activeSuggest}` : undefined}
                    style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 15, color: '#2C2C2C' }}
                  />
                  {searchInput && (
                    <button type="button" onClick={clearSearchBox} aria-label="Suche zurücksetzen" style={{ cursor: 'pointer', color: '#9A9494', fontSize: 14, background: 'none', border: 'none', padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
                  )}
                </div>
                {/* Autocomplete dropdown (full width under the field) */}
                {renderSuggestDropdown({ top: 'calc(100% + 6px)', left: 0, right: 0 }, 'lp-mobile')}
              </div>
            </div>

            {/* Schnellfilter als zwei wischbare Reihen (Vorbild Meetup): erst wann und wo, dann die
                Disziplin. „Alle“ nimmt Zeitraum, Ort und Disziplin zurück. Der gesetzte Ort steht im
                Chip „In meiner Nähe“, nicht mehr in einer eigenen Zeile. */}
            {/* Disziplin zuerst (häufigster Filter), darunter Ort, Zeitraum, Eventart und Distanz als Knöpfe;
                jeder öffnet sein Blatt von unten. Der Knopf „Filter“ und die Schublade entfallen. */}
            {!forcedCategory && <div className="lp-chipreihe lp-disziplinreihe">{chipsDisziplin}</div>}
            <div className="lp-chipreihe lp-filterreihe">
              {ortFeld(true)}{zeitraumFeld(true)}{eventartFeld}{distanzFeld}
              {activeFilters.some(f => !f.paused) && !listenTitel && (
                <button type="button" className="lp-zuruecksetzen" onClick={resetFilters}>Zurücksetzen</button>
              )}
            </div>

            {/* „Mit Freunden“ auf dem Handy: eine stille Zeile unter den Disziplinen, nur mit Freunden. */}
            {hasFriends && !permanentView && (
              <button type="button" role="switch" aria-checked={friendsOnly} onClick={() => setFriendsOnly(v => !v)} className="lp-freunde-schalter">
                <Users size={16} aria-hidden />
                <span>Nur Events mit Freunden</span>
                <span className={`lp-freunde-switch${friendsOnly ? ' lp-freunde-switch--an' : ''}`} aria-hidden />
              </button>
            )}

            {/* Überschrift nur in der Liste „Permanent“: ohne den Umschalter wäre sonst nicht zu
                sehen, welche Liste das ist. Auf der Startseite ist klar, dass kommende Events
                folgen. Veranstalter, Profi-Rennen und die permanenten Events erreicht man auf dem
                Handy über „Mehr“ in der Tab-Leiste (BottomTabBar). */}
            {permanentView && (
              <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 20, color: '#2C2C2C' }}>{PERMANENT_LANDING.heading}</span>
            )}

            {/* Ergebniszeile — die aktiven Filter zählt die Badge am Filter-Button, deshalb hier
                bewusst keine Chip-Reihe mehr (sie schob die Liste auf Phones weit nach unten). */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
              <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 20, color: '#2C2C2C' }}>{permanentView ? '' : listenTitel ?? 'Nächste Events'}</span>
              <span style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: '#922C57', whiteSpace: 'nowrap' }}>
                {totalElements === 1 ? '1 Event' : `${totalElements.toLocaleString('de-DE')} Events`}
              </span>
            </div>
            {offSeasonToggle}
          </div>

          {/* Überschrift (H1) klein über der Liste, nur am Rechner. Sie trägt auf den Regions- und
              Disziplinseiten das Suchwort („RTF Events in Hessen“) und bleibt deshalb; das eigene
              Band mit Einleitung darunter ist entfallen, die Einleitung steht in der
              Meta-Beschreibung und im Textteil unten. */}
          {/* Ergebniszeile statt Schlagzeile (Vorbild Tripadvisor „8,417 properties in Rome“): Zahl und
              Titel in einer Zeile, Leseschrift in Ink, kein farbiges Wort. Als Schlagzeile in Archivo
              mit „Event“ in Bordeaux wiederholte sie das Logo direkt darüber. Bleibt die H1, weil sie
              auf Regions- und Disziplinseiten das Suchwort trägt („Rennrad Events in Hessen“). */}
          {/* Unsichtbar, aber im Dokument: die H1 trägt auf Regions- und Disziplinseiten das Suchwort
              („Rennrad Events in Hessen“). Sichtbar stand sie als Wiederholung des Logos darüber. */}
          <div className="lp-titel sr-only">
            <h1>
              {(() => {
              const loc = selectedCity || (selectedBundesland ? blDisplay(selectedBundesland) : '');
              const evt = 'Events';
              if (eventTypeLanding) {
                return loc ? `${eventTypeLanding.label} in ${loc}`
                  : bannerCountryLabel ? `${eventTypeLanding.label} in ${bannerCountryLoc} ${currentYear}`
                  : `${eventTypeLanding.label} ${currentYear}`;
              }
              // Startseite ohne Filter: einladende Überschrift. Gefilterte Ansichten behalten die
              // keyword-reichen Titel (SEO für Kategorie-/Orts-Seiten).
              if (permanentView) {
                return <>{PERMANENT_LANDING.label}{loc ? <> in {loc}</> : null}</>;
              }
              if (!catHeadingLabel && !loc && !bannerCountryLabel) {
                return 'Radsport-Events';
              }
              const label = catHeroLabel ?? 'Rennrad, RTF & Gravel';
              if (loc) return <>{label} {evt} in {loc}</>;
              if (bannerCountryLabel) return <>{label} {evt} in {bannerCountryLoc} {currentYear}</>;
              return <>{label} {evt} {currentYear}</>;
            })()}
            </h1>
          </div>

          {/* Suchzeile am Rechner: oben die Suche über den Filtern, darunter Ort und Zeitraum als Felder
              und die Disziplinen als Chips (Rennrad, Gravel, MTB) mit den übrigen Schnellfiltern;
              „Filter“ öffnet alles Weitere. */}
          {showSeitenKarte && (
            <div className="lp-suchzeile">
              {listenTitel && (
                <p className="lp-listentitel"><span>{listenTitel}</span><span className="lp-listentitel-zahl">{totalElements === 1 ? '1 Event' : `${totalElements.toLocaleString('de-DE')} Events`}</span></p>
              )}
        <div ref={desktopSearchRef} style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${suggestOpen ? '#922C57' : '#E5E1E1'}`, borderRadius: 999, padding: '9px 16px', boxShadow: 'none' }}>
                    <Search size={17} color="#9A9494" aria-hidden style={{ flexShrink: 0 }} />
                    <input
                      value={searchInput}
                      onChange={e => onCombinedSearch(e.target.value)}
                      onFocus={focusSearch}
                      onKeyDown={onSearchKeyDown}
                      placeholder="Event oder Ort suchen"
                      aria-label="Suche nach Event, Ort, PLZ oder Bundesland"
                      role="combobox"
                      aria-expanded={suggestOpen && suggestOptions.length > 0}
                      aria-controls="lp-desktop-listbox"
                      aria-autocomplete="list"
                      aria-activedescendant={activeSuggest >= 0 ? `lp-desktop-opt-${activeSuggest}` : undefined}
                      style={{ width: '100%', minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 16, color: '#2C2C2C' }}
                    />
                    {searchInput && (
                      <button type="button" onClick={clearSearchBox} aria-label="Suche zurücksetzen" style={{ cursor: 'pointer', color: '#9A9494', flexShrink: 0, display: 'inline-flex', background: 'none', border: 'none', padding: 0, lineHeight: 1 }} ><X size={15} aria-hidden /></button>
                    )}
                  </div>
                  {renderSuggestDropdown({ top: 'calc(100% + 8px)', left: 0, right: 0 }, 'lp-desktop')}
                </div>
              {/* Disziplin zuerst und über die ganze Breite: die Frage, die jeder Besucher als erste beantwortet. */}
              {!forcedCategory && (
                <div className="lp-disziplinen" role="group" aria-label="Disziplin">
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                    const active = selectedCategory === key;
                    return (
                      <button key={key} type="button" aria-pressed={active} className={`lp-disziplin${active ? ' lp-disziplin--an' : ''}`}
                        style={{ ['--lp-farbe' as string]: catColorOf(key as EventCategory) }}
                        onClick={() => { setSelectedCategory(active ? '' : key); setSelectedEventTypes([]); setEaOpen(false); setEaQuery(''); }}>
                        <span className="lp-disziplin-raute" aria-hidden />{label}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="lp-felder">
                {ortFeld(false)}
                {zeitraumFeld(false)}
              </div>
              {/* Eventart und Distanz als Knöpfe unter Ort und Zeitraum (Vorbild Navan, komoot). Damit steht
                  jeder Filter der Schublade oben, und der Knopf „Filter“ entfällt am Rechner. */}
              <div className="lp-schnell">
                {/* Eventart mit Suche und allen Arten; ohne Disziplin zuerst Ultracycling, Bikepacking, 24h. */}
                {eventartFeld}
                {distanzFeld}
                {hasFriends && !permanentView && (
                  <button type="button" aria-pressed={friendsOnly} style={quickPill(friendsOnly, true)} onClick={() => setFriendsOnly(v => !v)}>
                    <Users size={15} aria-hidden />Mit Freunden
                  </button>
                )}
                {/* Trefferzahl rechts in derselben Zeile; auf Seiten mit Listen-Titel steht sie bei diesem. */}
                {/* Nur ohne Listen-Titel: auf den Seiten aus der Suche ist ein Teil des Filters die Seite selbst
                    (RTF, Hessen), den soll „Zurücksetzen“ nicht wegnehmen. Dort hat jedes Feld sein X. */}
                {!listenTitel && activeFilters.some(f => !f.paused) && (
                  <button type="button" className="lp-zuruecksetzen" onClick={resetFilters}>Zurücksetzen</button>
                )}
                {!listenTitel && <span className="lp-trefferzahl">{totalElements === 1 ? '1 Event' : `${totalElements.toLocaleString('de-DE')} Events`}</span>}
                {/* Liste, Karte oder beides; gemerkt im Browser (`radevents.startAnsicht`). */}
                <span className="lp-ansicht" role="group" aria-label="Ansicht">
                  {([['beide', Columns2, 'Liste und Karte'], ['liste', List, 'Nur Liste']] as const).map(([wert, Icon, name]) => (
                    <button key={wert} type="button" aria-pressed={ansicht === wert} title={name} aria-label={name}
                      className={ansicht === wert ? 'lp-ansicht-an' : undefined} onClick={() => setAnsicht(wert)}>
                      <Icon size={16} aria-hidden />
                    </button>
                  ))}
                </span>
              </div>
            </div>
          )}

          {/* Die Filter aus der Schublade, die nicht in der Leiste oben stehen; ohne solche keine Zeile. */}
          {activeFilters.some(f => (f.inSheet || f.paused) && !f.quick) && (
          <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: -6 }}>
            {activeFilters.filter(f => (f.inSheet || f.paused) && !f.quick).map((f, i) => (
              <span key={i} title={f.paused ? 'Zählt bei einer Textsuche nicht' : undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: f.paused ? '#F5F3F3' : '#fff', border: '1px solid #E5E1E1', borderRadius: 999, padding: '6px 12px', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 600, fontSize: 13, color: f.paused ? '#9A9494' : '#2C2C2C' }}>
                <span style={f.paused ? { textDecoration: 'line-through' } : undefined}>{f.label}</span>
                <button type="button" onClick={f.clear} aria-label={`${f.label} entfernen`} style={{ display: 'inline-flex', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9A9494' }}><X size={13} aria-hidden /></button>
              </span>
            ))}
          </div>
          )}
          {offSeasonToggle && <div className="hide-mobile">{offSeasonToggle}</div>}

          {/* Werbeplatz: welche Anzeigen laufen, schaltet die Redaktion unter /admin/werbeplatz.
              Mit Ankündigung steht er auf allen Geräten hier, die Ankündigung zuerst. Ohne steht
              er auf dem Handy nicht hier, sondern wiederkehrend in der Liste (`PROMO_EVERY`):
              über den ersten Events nahm er dort fast den ganzen ersten Bildschirm ein. */}

      {showDigestModal && (
        <DigestRegisterModal bundesland={selectedBundesland} onClose={() => setShowDigestModal(false)} />
      )}

          {/* Ein Treffer aus Tirol neben einem gesetzten 100-km-Umkreis braucht eine Erklaerung —
              ohne sie sieht die Liste nach einem kaputten Filter aus. */}
          {radiusPaused && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FCF5F8', border: '1px solid #F0DCE5', borderRadius: 12, padding: '10px 14px', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 13.5, lineHeight: 1.45, color: '#6A6A6A' }}>
              <span aria-hidden style={{ flexShrink: 0 }}>⌕</span>
              <span>Bei einer Textsuche zeigen wir Treffer <strong style={{ color: '#2C2C2C' }}>überall</strong> — der {selectedRadius} km Umkreis{locationLabel ? ` um ${locationLabel}` : ''} zählt dabei nicht.</span>
            </div>
          )}

          {/* Event cards */}
          {loading && events.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <div className="animate-spin" style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #E5E1E1', borderBottomColor: '#922C57' }} />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div style={{ padding: 50, textAlign: 'center', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, color: '#6A6A6A' }}>
              {/* Bei aktivem Umkreis den wahrscheinlichsten Grund zuerst nennen und genau ihn
                  wegnehmen lassen — „Zurücksetzen" wirft sonst auch die gerade getippte Suche weg,
                  und wer nach einem Namen sucht, meint meist genau dieses Event. */}
              {radiusApplies ? (
                <>
                  Keine Treffer im {selectedRadius} km Umkreis{locationLabel ? ` um ${locationLabel}` : ''}.{' '}
                  <span onClick={clearLocation} style={{ color: '#922C57', cursor: 'pointer', fontWeight: 700 }}>Ohne Umkreis suchen</span>
                </>
              ) : (
                <>
                  Keine Events für diese Filter. <span onClick={resetFilters} style={{ color: '#922C57', cursor: 'pointer', fontWeight: 700 }}>Zurücksetzen</span>
                </>
              )}
            </div>
          ) : (
            <div className="lp-zeilen" ref={zeilenRef}>
              {filteredEvents.map((event, i) => {
                const asStage = stageIndexes.has(i);
                const promoAfter = i % PROMO_EVERY === PROMO_FIRST - 1;
                return (
                  <React.Fragment key={event.id}>
                  {/* Neben der Karte als Zeile; eine gebuchte Hervorhebung bleibt die Card, die sie verspricht. */}
                  {!activePromotion(event) ? (
                    <div onMouseEnter={showSeitenKarte ? () => setHoverId(event.id) : undefined} onMouseLeave={showSeitenKarte ? () => setHoverId(null) : undefined}>
                      <EventZeile
                        event={event}
                        favorited={!!event.favorited}
                        onFavorite={(e) => { e.stopPropagation(); handleFavorite(event); }}
                        userLat={radiusActive ? effectiveLat : null}
                        userLng={radiusActive ? effectiveLng : null}
                        to={`/events/${event.id}`}
                        onNavigate={rememberListState}
                      />
                    </div>
                  ) : (
                  <article
                    onMouseEnter={showSeitenKarte ? () => setHoverId(event.id) : undefined}
                    onMouseLeave={showSeitenKarte ? () => setHoverId(null) : undefined}
                    /* Die Hervorhebung ändert nur die Hülle — Position in der Liste und
                       Sortierung bleiben unangetastet. Auch die Bühne rutscht nicht nach oben:
                       sie ist breiter, nicht früher. */
                    className={`event-card card-interactive ${promoCardClass(event, asStage)} ${isOffSeason(event) ? 'event-card--off-season' : ''} ${suppressAnim ? '' : `animate-fade-up stagger-${Math.min(i + 1, 6)}`}`}
                  >
                    {asStage ? (
                      <EventCardStage
                        event={event}
                        favorited={!!event.favorited}
                        favoriteTitle={event.favorited ? 'Favorit entfernen' : 'Als Favorit markieren'}
                        onFavorite={(e) => { e.stopPropagation(); handleFavorite(event); }}
                        userLat={radiusActive ? effectiveLat : null}
                        userLng={radiusActive ? effectiveLng : null}
                        to={`/events/${event.id}`}
                        onNavigate={rememberListState}
                      />
                    ) : (
                    <>
                    <EventCardHero
                      event={event}
                      favorited={!!event.favorited}
                      favoriteTitle={event.favorited ? 'Favorit entfernen' : 'Als Favorit markieren'}
                      onFavorite={(e) => { e.stopPropagation(); handleFavorite(event); }}
                    />

                    {/* Die Card öffnet über den Titel-Link (spannt sich über die ganze Card), damit
                        Mausrad- und Strg-Klick einen neuen Tab öffnen. Der Listenzustand wird nur
                        beim normalen Klick gesichert — bei einem neuen Tab bleibt die Liste ja
                        stehen und darf nicht überschrieben werden. */}
                    <EventCardBody
                      event={event}
                      userLat={radiusActive ? effectiveLat : null}
                      userLng={radiusActive ? effectiveLng : null}
                      to={`/events/${event.id}`}
                      onNavigate={rememberListState}
                    />
                    </>
                    )}
                  </article>
                  )}
                  {promoAfter && <div className="lp-cards-promo"><PromoBannerSlot /></div>}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* Infinite scroll sentinel + loading indicator */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {/* Platz für die nächsten drei Seiten, solange es weitere gibt: graue Zeilen in der Höhe der
              Events, die gleich kommen. Ohne ihn endete die Liste beim schnellen Scrollen kurz, die
              mitlaufende Karte rutschte mit dem Ende nach oben und sprang mit den neuen Events
              zurück, und darunter blitzte die Fußzeile auf. Geladen wird trotzdem nur, was man
              erreicht. */}
          {!loading && currentPage + 1 < totalPages && (
            <div className="lp-platzhalter" aria-hidden
              style={{ height: Math.min(3 * SEITE, Math.max(0, totalElements - events.length)) * zeilenHoehe, '--zeile': `${zeilenHoehe}px` } as React.CSSProperties} />
          )}
          {loadingMore && <span className="sr-only" role="status">Weitere Events werden geladen</span>}
          {/* Ende der Liste im Umkreis: sagen, dass es dahinter weitergeht. Sonst hält man die
              Events um zuhause für alle (30.10.2026). */}
          {radiusApplies && !loading && filteredEvents.length > 0 && currentPage + 1 >= totalPages && (() => {
            // Die nächste Stufe aus den Umkreis-Knöpfen des Ortsfelds; ab 200 km bleibt nur „Überall“.
            const weiter = RADIUS_PRESETS.find(km => km > (selectedRadius ?? 0));
            return (
              <section className="lp-umkreis-ende" aria-label="Weitere Events">
                <span className="lp-umkreis-ende__icon" aria-hidden><Radar size={22} /></span>
                <h2>Weiter weg gibt es mehr</h2>
                <p>Bis hier alle Events im Umkreis von {selectedRadius} km{locationLabel ? ` um ${locationLabel}` : ''}.</p>
                <div className="lp-umkreis-ende__knoepfe">
                  {weiter && <button type="button" className="lp-umkreis-ende__haupt" onClick={() => { markLocationOn(); setSelectedRadius(weiter); }}>Bis {weiter} km</button>}
                  <button type="button" onClick={clearLocation}><Globe size={16} aria-hidden />Überall</button>
                </div>
              </section>
            );
          })()}
        </div>{/* end right column */}

        {showSeitenKarte && (
          <aside className="lp-karte" aria-label="Karte der Events">
            {/* Teiler zum Verschieben; Doppelklick stellt das Verhältnis zurück. */}
            {ansicht === 'beide' && (
              <div className={`lp-teiler${teilerHinweis ? ' lp-teiler--hinweis' : ''}`} role="separator" aria-orientation="vertical" aria-label="Breite von Liste und Karte"
                title="Ziehen, um die Breite zu ändern. Doppelklick: zurücksetzen" onPointerDown={teilerZiehen} onDoubleClick={teilerZuruecksetzen}>
                <span />
              </div>
            )}
            {ansicht === 'beide' && teilerHinweis && <TeilerHinweis onClose={teilerHinweisWeg} />}
            <KarteMitHover
              hoverStore={hoverStore}
              key={permanentView ? 'permanent' : 'termine'}
              center={kartenOrt ?? { lat: 51.1, lng: 10.4 }}
              radiusKm={kartenOrt?.radiusKm ?? 100}
              radiusActive={kartenOrt != null}
              initialZoom={kartenOrt ? 8 : 6}
              points={kartenPoints}
              favoriteIds={favoriteIds}
              onOpenEvent={id => navigate(`/events/${id}`)}
              startBounds={KARTE_START}
              fitPointsKey={selectedBundesland || selectedCountry ? `${selectedCountry}|${selectedBundesland}` : undefined}
            />
          </aside>
        )}
      </div>{/* end lp-grid */}


      {isPhone && !handyKarte && (
        <button type="button" className="lp-karte-fab" onClick={() => { setHandyKarte(true); umamiTrack('handy-karte'); }}>
          <MapIcon size={17} aria-hidden />Karte
        </button>
      )}
      {isPhone && handyKarte && (
        <div className="lp-handykarte" role="dialog" aria-label="Karte der Events">
          <LocationRadiusMap
            key={permanentView ? 'permanent' : 'termine'}
            center={kartenOrt ?? { lat: 51.1, lng: 10.4 }}
            radiusKm={kartenOrt?.radiusKm ?? 100}
            radiusActive={kartenOrt != null}
            initialZoom={kartenOrt ? 8 : 5}
            points={kartenPoints}
            favoriteIds={favoriteIds}
            compact
            onOpenEvent={id => navigate(`/events/${id}`)}
            startBounds={KARTE_START}
            fitPointsKey={selectedBundesland || selectedCountry ? `${selectedCountry}|${selectedBundesland}` : undefined}
          />
          {/* Dieselben Filter wie über der Liste, in einer wischbaren Reihe (Vorbild Airbnb). */}
          <div className="lp-handykarte-kopf">
            {!forcedCategory && <div className="lp-chipreihe lp-disziplinreihe">{chipsDisziplin}</div>}
            <div className="lp-chipreihe lp-filterreihe">
              {ortFeld(true)}{zeitraumFeld(true)}{eventartFeld}{distanzFeld}
            </div>
            <div className="lp-handykarte-zahl">{totalElements === 1 ? '1 Event' : `${totalElements.toLocaleString('de-DE')} Events`}{kartenOrt ? ` · ${kartenOrt.label}` : ''}</div>
          </div>
          <button type="button" className="lp-karte-fab lp-karte-fab--zurueck" onClick={() => setHandyKarte(false)}>
            <List size={17} aria-hidden />Liste
          </button>
        </div>
      )}


      <EventVerzeichnis />
      {/* Kilometer eintragen aus dem Werbeplatz: außerhalb des Wechsels, siehe `openQuickRide`. */}
      <QuickRideHost />

      {/* Event eintragen bzw. Bearbeitung vorschlagen (Neugestaltung 30.10.2026). Gruppen statt
          einer Spalte mit 30 Feldern, am Handy Vollbild mit festem Kopf und Einreichen-Knopf unten.
          Die Felder und ihre Logik sind unverändert; nur Reihenfolge, Gruppierung und Form. */}
      {showProposeModal && (
        <div className="vf-overlay" role="dialog" aria-modal="true" aria-labelledby="vf-titel">
          <div className="vf-blatt">
            <div className="vf-kopf">
              <h2 id="vf-titel" className="vf-titel">{proposeEditEventId ? 'Bearbeitung vorschlagen' : 'Event eintragen'}</h2>
              <button type="button" className="vf-schliessen" onClick={() => setShowProposeModal(false)} aria-label="Schließen"><X size={20} aria-hidden /></button>
            </div>

            <div className="vf-inhalt">
              {proposeZusammenfassung ? (
                <p className="vf-zusammenfassung" role="alert"><CircleAlert size={17} aria-hidden />{proposeZusammenfassung}</p>
              ) : (
                <p className="vf-pflicht">Felder mit <span className="vf-stern">*</span> brauchen wir. Alles andere hilft, ist aber freiwillig.</p>
              )}

              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Was</h3>
                <div className="vf-feld">
                  <label className="vf-label" htmlFor="vf-title">Titel <span className="vf-stern">*</span></label>
                  <input id="vf-title" value={proposeForm.title} onChange={e => setProposeForm(f => ({ ...f, title: e.target.value }))}
                    className={vfKlasse('title')} {...vfAria('title')} placeholder="z. B. RTF Rund um den Odenwald" />
                  {vfFehler('title')}
                </div>
                <div className="vf-reihe">
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-category">Kategorie</label>
                    <select id="vf-category" value={proposeForm.category}
                      onChange={e => {
                        const cat = e.target.value as EventCategory;
                        // Eventart nur behalten, wenn sie zur neuen Kategorie passt (wie im EventFormModal)
                        setProposeForm(f => ({ ...f, category: cat,
                          secondaryCategories: f.secondaryCategories.filter(c => c !== cat),
                          eventType: (EVENT_TYPES_BY_CATEGORY[cat] || []).includes(f.eventType as string) ? f.eventType : '' }));
                      }}
                      className="vf-eingabe">
                      {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-type">Eventart</label>
                    <select id="vf-type" value={proposeForm.eventType} onChange={e => setProposeForm(f => ({ ...f, eventType: e.target.value as EventType | '' }))}
                      className="vf-eingabe">
                      <option value="">Bitte wählen</option>
                      {sortEventTypesByLabel(EVENT_TYPES_BY_CATEGORY[proposeForm.category] || []).map(key => (
                        <option key={key} value={key}>{EVENT_TYPE_LABELS[key] ?? key}</option>
                      ))}
                      {/* Übernommenen Legacy-Typ anzeigen, auch wenn er nicht mehr in der Kategorie-Liste steht */}
                      {proposeForm.eventType && !(EVENT_TYPES_BY_CATEGORY[proposeForm.category] || []).includes(proposeForm.eventType as string) && (
                        <option value={proposeForm.eventType}>{EVENT_TYPE_LABELS[proposeForm.eventType] ?? proposeForm.eventType}</option>
                      )}
                    </select>
                  </div>
                </div>
                {/* Eine Veranstaltung, die z. B. mit Rennrad, Gravel und MTB gefahren werden kann,
                    wird einmal eingetragen und nicht dreimal. */}
                <div className="vf-feld">
                  <span className="vf-label">Auch offen für</span>
                  <div className="vf-haken-reihe">
                    {EVENT_CATEGORY_ORDER.filter(c => c !== proposeForm.category).map(c => (
                      <label key={c} className="vf-haken">
                        <input type="checkbox" checked={proposeForm.secondaryCategories.includes(c)}
                          onChange={e => setProposeForm(f => ({
                            ...f,
                            secondaryCategories: e.target.checked
                              ? [...f.secondaryCategories, c]
                              : f.secondaryCategories.filter(x => x !== c),
                          }))} />
                        {CATEGORY_LABELS[c] ?? c}
                      </label>
                    ))}
                  </div>
                </div>
              </section>

              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Wann</h3>
                <label className="vf-haken">
                  <input type="checkbox" checked={proposeForm.permanent}
                    onChange={e => setProposeForm(f => ({ ...f, permanent: e.target.checked, seasonFrom: null, seasonUntil: null, areaName: '', ...(e.target.checked ? { eventDate: '', endDate: '', tentative: false } : {}) }))} />
                  Jederzeit fahrbar, ohne festen Termin
                </label>
                {proposeForm.permanent ? (<>
                  <SeasonSelect idPrefix="propose" from={proposeForm.seasonFrom} until={proposeForm.seasonUntil}
                    onChange={(seasonFrom, seasonUntil) => setProposeForm(f => ({ ...f, seasonFrom, seasonUntil }))}
                    labelClassName="vf-label" selectClassName="vf-eingabe" />
                  <AreaField idPrefix="propose" value={proposeForm.areaName} onChange={areaName => setProposeForm(f => ({ ...f, areaName }))}
                    labelClassName="vf-label" inputClassName="vf-eingabe" />
                </>) : (
                  <div className="vf-reihe vf-reihe--drei">
                    <div className="vf-feld">
                      <label className="vf-label" htmlFor="vf-date">Datum <span className="vf-stern">*</span></label>
                      <input id="vf-date" type="date" value={proposeForm.eventDate} onChange={e => setProposeForm(f => ({ ...f, eventDate: e.target.value }))}
                        className={vfKlasse('eventDate')} {...vfAria('eventDate')} />
                      {vfFehler('eventDate')}
                    </div>
                    <div className="vf-feld">
                      <label className="vf-label" htmlFor="vf-end">Bis</label>
                      <input id="vf-end" type="date" value={proposeForm.endDate} min={proposeForm.eventDate || undefined}
                        onChange={e => setProposeForm(f => ({ ...f, endDate: e.target.value }))}
                        className={vfKlasse('endDate')} {...vfAria('endDate')} />
                      {vfFehler('endDate')}
                    </div>
                    <div className="vf-feld">
                      <label className="vf-label" htmlFor="vf-time">Start</label>
                      <input id="vf-time" type="time" value={proposeForm.startTime || ''} onChange={e => setProposeForm(f => ({ ...f, startTime: e.target.value }))}
                        className="vf-eingabe" />
                    </div>
                  </div>
                )}
                {proposeForm.permanent && (
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-time">Start</label>
                    <input id="vf-time" type="time" value={proposeForm.startTime || ''} onChange={e => setProposeForm(f => ({ ...f, startTime: e.target.value }))}
                      className="vf-eingabe vf-eingabe--kurz" />
                  </div>
                )}
                {!proposeForm.permanent && (
                  <label className="vf-haken">
                    <input type="checkbox" checked={proposeForm.tentative}
                      onChange={e => setProposeForm(f => ({ ...f, tentative: e.target.checked }))} />
                    Termin ist noch vorläufig
                  </label>
                )}
              </section>

              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Wo</h3>
                <div className="vf-feld">
                  <label className="vf-label" htmlFor="vf-loc">Treffpunkt</label>
                  <input id="vf-loc" value={proposeForm.locationName} onChange={e => setProposeForm(f => ({ ...f, locationName: e.target.value }))}
                    className="vf-eingabe" placeholder="z. B. Sporthalle am Markt" />
                </div>
                <div className="vf-reihe vf-reihe--strasse">
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-street">Straße</label>
                    <input id="vf-street" value={proposeForm.street} onChange={e => setProposeForm(f => ({ ...f, street: e.target.value }))}
                      className="vf-eingabe" />
                  </div>
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-nr">Nr.</label>
                    <input id="vf-nr" value={proposeForm.houseNumber} onChange={e => setProposeForm(f => ({ ...f, houseNumber: e.target.value }))}
                      className="vf-eingabe" />
                  </div>
                </div>
                <div className="vf-reihe vf-reihe--plz">
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-plz">PLZ {!proposeForm.permanent && <span className="vf-stern">*</span>}</label>
                    <input id="vf-plz" inputMode="numeric" value={proposeForm.postalCode} onChange={e => setProposeForm(f => ({ ...f, postalCode: e.target.value }))}
                      className={vfKlasse('postalCode')} {...vfAria('postalCode')} />
                    {vfFehler('postalCode')}
                  </div>
                  <div className="vf-feld">
                    <label className="vf-label" htmlFor="vf-city">Ort {!proposeForm.permanent && <span className="vf-stern">*</span>}</label>
                    <input id="vf-city" value={proposeForm.city} onChange={e => setProposeForm(f => ({ ...f, city: e.target.value }))}
                      className={vfKlasse('city')} {...vfAria('city')} />
                    {vfFehler('city')}
                  </div>
                </div>
                <div className="vf-feld">
                  <label className="vf-label" htmlFor="vf-land">Bundesland oder Land <span className="vf-stern">*</span></label>
                  <select id="vf-land" value={proposeForm.bundesland} onChange={e => setProposeForm(f => ({ ...f, bundesland: e.target.value }))}
                    className={vfKlasse('bundesland')} {...vfAria('bundesland')}>
                    <option value="">Bitte wählen</option>
                    <BundeslandOptions />
                  </select>
                  {vfFehler('bundesland')}
                </div>
                {proposeForm.permanent && (
                  <p className="vf-hilfe">Mehrere Einstiegspunkte? Dann PLZ und Ort leer lassen und die Einstiegspunkte in der Beschreibung nennen.</p>
                )}
                {/* Siehe EventFormModal: Hinweis, keine Sperre. Aus dem (Bundes)Land leitet sich
                    ab, in welchem Land die Adresse gesucht wird. */}
                {postalCodeRegionWarning(proposeForm.postalCode, proposeForm.bundesland) && (
                  <p className="vf-warnung">{postalCodeRegionWarning(proposeForm.postalCode, proposeForm.bundesland)}</p>
                )}
              </section>

              <section className="vf-gruppe">
                <div className="vf-gruppe-kopf">
                  <h3 className="vf-gruppe-titel">Strecken</h3>
                  <button type="button" onClick={addProposeDistance} className="vf-hinzufuegen"><Plus size={16} aria-hidden />Strecke</button>
                </div>
                {proposeForm.distances.map((d, idx) => (
                  <div key={idx} className="vf-strecke">
                    <div className="vf-strecke-werte">
                      <label className="vf-feld">
                        <span className="vf-label">km</span>
                        <input type="number" inputMode="decimal" value={d.distanceKm || ''}
                          onChange={e => updateProposeDistance(idx, 'distanceKm', parseFloat(e.target.value) || 0)}
                          className="vf-eingabe" />
                      </label>
                      <label className="vf-feld">
                        <span className="vf-label">Höhenmeter</span>
                        <input type="number" inputMode="numeric" value={d.elevationMeters || ''}
                          onChange={e => updateProposeDistance(idx, 'elevationMeters', parseInt(e.target.value) || 0)}
                          className="vf-eingabe" />
                      </label>
                      <div className="vf-feld" title="Zeitlimit, z. B. bei 24-Stunden-Rennen">
                        <span className="vf-label">Zeitlimit</span>
                        <div className="vf-dauer">
                          <input type="number" min="0" aria-label="Stunden" placeholder="Std." value={d.durationMinutes ? Math.floor(d.durationMinutes / 60) || '' : ''}
                            onChange={e => { const h = parseInt(e.target.value) || 0; const dm = h * 60 + ((d.durationMinutes ?? 0) % 60); updateProposeDistance(idx, 'durationMinutes', dm > 0 ? dm : 0); }}
                            className="vf-eingabe" />
                          <input type="number" min="0" max="59" aria-label="Minuten" placeholder="Min." value={d.durationMinutes ? d.durationMinutes % 60 || '' : ''}
                            onChange={e => { const m = parseInt(e.target.value) || 0; const dm = Math.floor((d.durationMinutes ?? 0) / 60) * 60 + m; updateProposeDistance(idx, 'durationMinutes', dm > 0 ? dm : 0); }}
                            className="vf-eingabe" />
                        </div>
                      </div>
                    </div>
                    <div className="vf-strecke-unten">
                      <input value={d.routeUrl || ''} placeholder="Link zur Strecke (Komoot, AllTrails)" aria-label="Link zur Strecke"
                        onChange={e => updateProposeDistance(idx, 'routeUrl', e.target.value)}
                        className="vf-eingabe" />
                      {proposeForm.distances.length > 1 && (
                        <button type="button" onClick={() => removeProposeDistance(idx)} className="vf-entfernen" aria-label="Strecke entfernen">
                          <Trash2 size={17} aria-hidden />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </section>

              {/* Wie im Redaktions-Formular: alles, was auf eine Seite führt, steht beieinander. */}
              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Anmeldung und Links</h3>
                <div className="vf-feld">
                  <label className="vf-label" htmlFor="vf-url">Website zum Event</label>
                  <input id="vf-url" type="url" value={proposeForm.organizerUrl} onChange={e => setProposeForm(f => ({ ...f, organizerUrl: e.target.value }))}
                    className="vf-eingabe" placeholder="https://" />
                </div>
                {/* Anmeldestart: steht meist in derselben Ausschreibung wie der Termin. Wir zeigen ihn
                    nicht öffentlich (siehe RegistrationFields), sammeln ihn aber gern mit ein. */}
                <RegistrationFields
                  value={{
                    mode: proposeForm.registrationMode,
                    start: proposeForm.registrationStart,
                    state: proposeForm.registrationState,
                    // Der Anmelde-Link ist der REGISTRATION-Link des Events, keine zweite Ablage.
                    url: registrationLinkOf(proposeForm.links),
                  }}
                  onChange={next => setProposeForm(f => ({
                    ...f,
                    registrationMode: next.mode,
                    registrationStart: next.start,
                    registrationState: next.state,
                    links: withRegistrationLink(f.links, next.url),
                  }))}
                  internal={false}
                  className="vf-feld"
                  inputStyle={proposeInputStyle}
                  labelStyle={proposeLabelStyle}
                />
                <div className="vf-feld">
                  <span className="vf-label">Weitere Links, z. B. die Ausschreibung</span>
                  <EventLinksEditor
                    links={proposeForm.links}
                    onChange={links => setProposeForm(f => ({ ...f, links }))}
                    allowInternal={false}
                    inputStyle={proposeInputStyle}
                  />
                </div>
              </section>

              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Veranstalter</h3>
                <div className="vf-segment" role="group" aria-label="Veranstalter angeben">
                  {(['existing', 'new', 'freetext'] as const).map(mode => (
                    <button key={mode} type="button" aria-pressed={organizerMode === mode}
                      onClick={() => setOrganizerMode(mode as typeof organizerMode)}>
                      {mode === 'existing' ? 'Aus der Liste' : mode === 'new' ? 'Neu anlegen' : 'Nur Name'}
                    </button>
                  ))}
                </div>
                {organizerMode === 'freetext' && (
                  <input value={proposeForm.organizer} onChange={e => setProposeForm(f => ({ ...f, organizer: e.target.value }))}
                    placeholder="Name des Veranstalters" aria-label="Name des Veranstalters" className="vf-eingabe" />
                )}
                {organizerMode === 'existing' && (
                  <div className="vf-feld">
                    <input value={organizerSearch} onChange={e => setOrganizerSearch(e.target.value)}
                      placeholder="Veranstalter suchen" aria-label="Veranstalter suchen" className="vf-eingabe" />
                    <select value={selectedOrganizerId ?? ''} aria-label="Veranstalter"
                      onChange={e => setSelectedOrganizerId(e.target.value ? Number(e.target.value) : null)}
                      className="vf-eingabe vf-liste" size={4}>
                      <option value="">Bitte wählen</option>
                      {organizers
                        .filter(o => !organizerSearch || o.name.toLowerCase().includes(organizerSearch.toLowerCase()))
                        .map(o => (
                          <option key={o.id} value={o.id}>{o.name}{o.contactPerson ? ` · ${o.contactPerson}` : ''}</option>
                        ))}
                    </select>
                  </div>
                )}
                {organizerMode === 'new' && (
                  <div className="vf-reihe">
                    <div className="vf-feld vf-ganz">
                      <input id="vf-org-name" value={newOrganizerName} onChange={e => setNewOrganizerName(e.target.value)}
                        placeholder="Name *" aria-label="Name des Veranstalters" className={vfKlasse('newOrganizerName')} {...vfAria('newOrganizerName')} />
                      {vfFehler('newOrganizerName')}
                    </div>
                    <input value={newOrganizerContactPerson} onChange={e => setNewOrganizerContactPerson(e.target.value)}
                      placeholder="Ansprechpartner" aria-label="Ansprechpartner" className="vf-eingabe" />
                    <input type="email" value={newOrganizerEmail} onChange={e => setNewOrganizerEmail(e.target.value)}
                      placeholder="E-Mail" aria-label="E-Mail des Veranstalters" className="vf-eingabe" />
                    <input type="tel" value={newOrganizerPhone} onChange={e => setNewOrganizerPhone(e.target.value)}
                      placeholder="Telefon" aria-label="Telefon" className="vf-eingabe" />
                    <input type="url" value={newOrganizerWebsite} onChange={e => setNewOrganizerWebsite(e.target.value)}
                      placeholder="Website" aria-label="Website des Veranstalters" className="vf-eingabe" />
                  </div>
                )}
              </section>

              <section className="vf-gruppe">
                <h3 className="vf-gruppe-titel">Mehr zum Event</h3>
                <div className="vf-feld">
                  <label className="vf-label" htmlFor="vf-desc">Beschreibung</label>
                  <textarea id="vf-desc" value={proposeForm.description} onChange={e => setProposeForm(f => ({ ...f, description: e.target.value }))}
                    rows={4} className="vf-eingabe vf-text" />
                </div>
                <div className="vf-haken-reihe">
                  <label className="vf-haken">
                    <input type="checkbox" checked={proposeForm.fullyBooked}
                      onChange={e => setProposeForm(f => ({ ...f, fullyBooked: e.target.checked }))} />
                    Ausgebucht
                  </label>
                  <label className="vf-haken">
                    <input type="checkbox" checked={proposeForm.cancelled}
                      onChange={e => setProposeForm(f => ({ ...f, cancelled: e.target.checked }))} />
                    Abgesagt
                  </label>
                </div>
              </section>

              {/* Kontakt des Einreichenden, nur ohne Anmeldung: angemeldet nimmt das Backend die
                  Konto-Adresse für die Rückmeldung. */}
              {!isAuthenticated && (
                <section className="vf-gruppe">
                  <h3 className="vf-gruppe-titel">Von dir</h3>
                  <div className="vf-reihe">
                    <div className="vf-feld">
                      <label className="vf-label" htmlFor="vf-name">Name</label>
                      <input id="vf-name" value={proposeForm.submitterName} autoComplete="name"
                        onChange={e => setProposeForm(f => ({ ...f, submitterName: e.target.value }))}
                        className="vf-eingabe" />
                    </div>
                    <div className="vf-feld">
                      <label className="vf-label" htmlFor="vf-mail">E-Mail</label>
                      <input id="vf-mail" type="email" value={proposeForm.submitterEmail} autoComplete="email"
                        onChange={e => setProposeForm(f => ({ ...f, submitterEmail: e.target.value }))}
                        className={vfKlasse('submitterEmail')} {...vfAria('submitterEmail')} />
                      {vfFehler('submitterEmail')}
                    </div>
                  </div>
                  <p className="vf-hilfe">Freiwillig. Dann melden wir uns, sobald das Event online ist, oder bei Rückfragen. Wir veröffentlichen beides nicht.</p>
                </section>
              )}
            </div>

            {proposeServerFehler && <p className="vf-serverfehler" role="alert"><CircleAlert size={17} aria-hidden />{proposeServerFehler}</p>}
            <div className="vf-fuss">
              <button type="button" onClick={() => setShowProposeModal(false)} className="vf-abbrechen">Abbrechen</button>
              <button type="button" onClick={handleProposeSave} disabled={proposeSaving} className="vf-senden">
                {proposeSaving ? 'Wird eingereicht …' : proposeEditEventId ? 'Vorschlag einreichen' : 'Event einreichen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
